import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Database,
  Search,
  Edit3,
  CheckCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  FileText,
  Filter,
  X,
  XCircle,
  Eye,
  Award,
  TrendingUp,
  BarChart3,
  Layers,
  Loader2,
  RefreshCw,
  AlertCircle,
  MessageSquare,
  BookOpen,
  Zap,
  Brain,
  Tag,
  Hash,
  Star,
  Target,
  Timer,
  ShieldAlert,
  Download,
  Trash2,
  Calendar,
  GitBranch,
  // Lightbulb,
  // CheckSquare,
  // Square,
} from 'lucide-react';
import Swal from 'sweetalert2';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge, Badge } from '../../components/Badge';
import { EmptyState } from '../../components/EmptyState';
import { InteractiveQuestionPlayer } from '../../components/InteractiveQuestionPlayer';
import { ViewToggle } from '../../components/ViewToggle';
import { SortableTable, Column } from '../../components/SortableTable';
import { Tooltip } from '../../components/Tooltip';
import { CreateVersionModal } from '../../components/versioning/CreateVersionModal';
import { VersionHistoryModal } from '../../components/versioning/VersionHistoryModal';
import { pdfAnalysisService } from '../../services/pdf-analysis.service';
import { freeSourceService } from '../../services/free-source.service';
import type { FreeSourceSyncStatus } from '../../services/free-source.service';
import { portfolioService } from '../../services/portfolio.service';
import TaxonomyCascade from '../../components/TaxonomyCascade';
import { confirmDelete, showDeleteSuccess, showDeleteError } from '../../utils/deleteConfirmation';
import { RichText, richTextToPlain, ensureEndPeriodRich } from '../../utils/richTextDisplay';
import { useAuthStore } from '../../store/authStore';
// import { storage } from '../../utils/localStorage';

const DIFFICULTIES = ['easy', 'medium', 'hard'];
const TYPES = [
  { value: 'multiple_choice', label: 'MCQ' },
  { value: 'short_answer', label: 'Short Answer' },
  { value: 'case_study', label: 'Case Study' },
  { value: 'interactive', label: 'Interactive' },
];
const STATUSES = [
  { value: 'generated', label: 'Generated' },
  { value: 'draft', label: 'Draft' },
  { value: 'approved', label: 'Approved' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'rejected', label: 'Rejected' },
];

// Type styling config
const TYPE_CONFIG: Record<string, { icon: React.ElementType; color: string; bg: string; border: string; label: string }> = {
  multiple_choice: { icon: CheckCircle, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', label: 'MCQ' },
  MCQ: { icon: CheckCircle, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', label: 'MCQ' },
  short_answer: { icon: Edit3, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', label: 'Short Answer' },
  case_study: { icon: BookOpen, color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200', label: 'Case Study' },
  interactive: { icon: MessageSquare, color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200', label: 'Interactive' },
};

const COGNITIVE_LABELS: Record<string, { label: string; color: string }> = {
  knowledge: { label: 'Knowledge', color: 'text-sky-600 bg-sky-50' },
  comprehension: { label: 'Comprehension', color: 'text-teal-600 bg-teal-50' },
  application: { label: 'Application', color: 'text-indigo-600 bg-indigo-50' },
  analysis: { label: 'Analysis', color: 'text-rose-600 bg-rose-50' },
};

export function PdfQuestionBank() {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  // Check permissions
  const canEdit = user?.permissions?.includes('question_bank_edit') || false;
  const canDelete = user?.permissions?.includes('question_bank_delete') || false;

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [oeCodeFilter, setOeCodeFilter] = useState('');
  const [questionIdFilter, setQuestionIdFilter] = useState('');
  const [portfolioFilter, setPortfolioFilter] = useState('all'); // NQG-RQ-29
  const [taxonomyNodeFilter, setTaxonomyNodeFilter] = useState<string | null>(null); // NQG-RQ-31
  // const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [detailQuestion, setDetailQuestion] = useState<any | null>(null);

  // Question versioning (NQG-RQ-27)
  const [versionTarget, setVersionTarget] = useState<any | null>(null); // question for CreateVersionModal
  const [versionsViewId, setVersionsViewId] = useState<string | null>(null); // question id for VersionHistoryModal

  // Sync to Main Bank (NQG-RQ-26 v2)
  const queryClient = useQueryClient();
  const [syncing, setSyncing] = useState(false);
  const { data: syncStatus } = useQuery<FreeSourceSyncStatus>({
    queryKey: ['free-source-sync-status'],
    queryFn: () => freeSourceService.getSyncStatus(),
    retry: false,
  });

  // NQG-RQ-29: portfolios for the filter dropdown
  const { data: portfolios = [] } = useQuery({
    queryKey: ['portfolios'],
    queryFn: () => portfolioService.list(),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  // NQG-RQ-31: selected portfolio's id drives the taxonomy cascade; changing
  // portfolio resets the taxonomy selection (per-portfolio tree)
  const selectedPortfolioId = useMemo(
    () => portfolios.find((p) => p.name === portfolioFilter)?._id || null,
    [portfolios, portfolioFilter]
  );
  useEffect(() => {
    setTaxonomyNodeFilter(null);
  }, [portfolioFilter]);

  // Data state
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [questions, setQuestions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 0 });

  // Excel Export Range
  const [exportFromQuestion, setExportFromQuestion] = useState<number>(1);
  const [exportToQuestion, setExportToQuestion] = useState<number>(0); // 0 means export all

  // Excel Export Date Range
  const [exportFromDate, setExportFromDate] = useState<string>('');
  const [exportToDate, setExportToDate] = useState<string>('');

  // Batch selection for bulk operations
  const [selectedQuestions, setSelectedQuestions] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // View mode
  const [view, setView] = useState<'grid' | 'list'>('list');

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // NWFE-RQ-01.6: questions keep their formatting (toolbar HTML + auto-italic
  // foreign terms) — detail views render it via <RichText/>, line-clamped
  // snippets use richTextToPlain()

  // Fetch questions from API
  const fetchQuestions = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const result = await pdfAnalysisService.getGeneratedQuestions({
        search: debouncedSearch || undefined,
        difficulty: difficultyFilter !== 'all' ? difficultyFilter : undefined,
        type: typeFilter !== 'all' ? typeFilter : undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        oeCode: oeCodeFilter || undefined,
        questionId: questionIdFilter || undefined,
        portfolio: portfolioFilter !== 'all' ? portfolioFilter : undefined,
        taxonomyNode: taxonomyNodeFilter || undefined, // NQG-RQ-31
        page,
        limit: itemsPerPage,
      });
      setQuestions(result.questions || []);
      setPagination(result.pagination);
      // Scroll to top when page changes
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      console.error('[QuestionBank] Fetch error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load questions');
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, difficultyFilter, typeFilter, statusFilter, oeCodeFilter, questionIdFilter, portfolioFilter, taxonomyNodeFilter, itemsPerPage]);

  // Handle items per page change
  const handleItemsPerPageChange = (newLimit: number) => {
    setItemsPerPage(newLimit);
    setPagination(prev => ({ ...prev, page: 1, limit: newLimit }));
    // Fetch will be triggered by useEffect when itemsPerPage changes
  };

  // Refetch when filters change
  useEffect(() => {
    fetchQuestions(1);
  }, [fetchQuestions]);

  const clearFilters = () => {
    setSearchTerm('');
    setDifficultyFilter('all');
    setTypeFilter('all');
    setStatusFilter('all');
    setOeCodeFilter('');
    setQuestionIdFilter('');
    setPortfolioFilter('all');
    setTaxonomyNodeFilter(null); // NQG-RQ-31
  };

  const hasActiveFilters = searchTerm || difficultyFilter !== 'all' || typeFilter !== 'all' || statusFilter !== 'all' || oeCodeFilter !== '' || questionIdFilter !== '' || portfolioFilter !== 'all' || !!taxonomyNodeFilter;

  // Helper functions for grid/list view
  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'MCQ':
      case 'multiple_choice':
        return <Target className="w-4 h-4" />;
      case 'case_study':
        return <BookOpen className="w-4 h-4" />;
      case 'short_answer':
        return <Edit3 className="w-4 h-4" />;
      case 'interactive':
        return <MessageSquare className="w-4 h-4" />;
      case 'essay':
        return <FileText className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'multiple_choice':
        return 'Multiple Choice';
      case 'MCQ':
        return 'MCQ';
      case 'case_study':
        return 'Case Study';
      case 'short_answer':
        return 'Short Answer';
      case 'interactive':
        return 'Interactive';
      case 'essay':
        return 'Essay';
      default:
        return type;
    }
  };

  // Flag question as AI wrong
  // const handleFlag = async (q: any) => {
  //   const result = await Swal.fire({
  //     title: 'Flag as AI Wrong?',
  //     html: `<p class="text-sm text-gray-600">Mark this question as incorrectly generated by AI.</p>
  //            <p class="text-xs text-gray-400 mt-2 font-mono">${q.oeCode || ''} — ${(q.stem || '').substring(0, 80)}...</p>`,
  //     icon: 'warning',
  //     showCancelButton: true,
  //     confirmButtonText: 'Flag as Wrong',
  //     cancelButtonText: 'Cancel',
  //     confirmButtonColor: '#dc2626',
  //     customClass: { popup: 'rounded-2xl' },
  //   });
  //   if (!result.isConfirmed) return;
  //   try {
  //     await pdfAnalysisService.updateGeneratedQuestion(q._id, { status: 'rejected', validationStatus: 'ai_wrong' });
  //     Swal.fire({ title: 'Flagged!', text: 'Question marked as AI wrong.', icon: 'success', timer: 1500, showConfirmButton: false });
  //     fetchQuestions(pagination.page);
  //   } catch (err: any) {
  //     Swal.fire({ title: 'Error', text: err.message || 'Failed to flag question.', icon: 'error', confirmButtonColor: '#dc2626' });
  //   }
  // };

  // Delete question
  const handleDelete = async (q: any) => {
    const confirmed = await confirmDelete({
      title: 'Delete Question?',
      itemName: `${q.oeCode || ''} — ${richTextToPlain(q.stem).substring(0, 80)}...`,
    });

    if (!confirmed) return;

    try {
      await pdfAnalysisService.deleteGeneratedQuestion(q._id);
      showDeleteSuccess('The question has been successfully deleted.');
      fetchQuestions(pagination.page);
    } catch (err: any) {
      showDeleteError(err.response?.data?.error || 'Failed to delete question');
    }
  };

  // Toggle question selection
  const toggleQuestionSelection = (questionId: string) => {
    const newSelected = new Set(selectedQuestions);
    if (newSelected.has(questionId)) {
      newSelected.delete(questionId);
    } else {
      newSelected.add(questionId);
    }
    setSelectedQuestions(newSelected);
    setSelectAll(newSelected.size === questions.length && questions.length > 0);
  };

  // Toggle select all
  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedQuestions(new Set());
      setSelectAll(false);
    } else {
      const allIds = new Set(questions.map(q => q._id));
      setSelectedQuestions(allIds);
      setSelectAll(true);
    }
  };

  // Clear selection when page or filters change
  useEffect(() => {
    setSelectedQuestions(new Set());
    setSelectAll(false);
  }, [pagination.page, debouncedSearch, difficultyFilter, typeFilter, statusFilter, oeCodeFilter, questionIdFilter, portfolioFilter, taxonomyNodeFilter]);

  // Batch approve selected questions
  const handleBatchApprove = async () => {
    if (selectedQuestions.size === 0) {
      Swal.fire({
        title: 'No Selection',
        text: 'Please select at least one question to approve.',
        icon: 'warning',
        confirmButtonColor: '#1e73be',
      });
      return;
    }

    const result = await Swal.fire({
      title: 'Approve Selected Questions?',
      html: `<p class="text-gray-600">You are about to approve <strong>${selectedQuestions.size}</strong> question(s).</p>`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, approve',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#10b981',
      customClass: { popup: 'rounded-2xl' },
    });

    if (!result.isConfirmed) return;

    try {
      await pdfAnalysisService.batchUpdateQuestionStatus({
        questionIds: Array.from(selectedQuestions),
        status: 'approved',
      });

      Swal.fire({
        title: 'Success!',
        text: `${selectedQuestions.size} question(s) have been approved.`,
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
      });

      setSelectedQuestions(new Set());
      setSelectAll(false);
      fetchQuestions(pagination.page);
    } catch (err: any) {
      Swal.fire({
        title: 'Error',
        text: err.response?.data?.error || 'Failed to approve questions',
        icon: 'error',
        confirmButtonColor: '#dc2626',
      });
    }
  };

  // Batch reject selected questions
  const handleBatchReject = async () => {
    if (selectedQuestions.size === 0) {
      Swal.fire({
        title: 'No Selection',
        text: 'Please select at least one question to reject.',
        icon: 'warning',
        confirmButtonColor: '#1e73be',
      });
      return;
    }

    const result = await Swal.fire({
      title: 'Reject Selected Questions?',
      html: `
        <p class="text-gray-600 mb-4">You are about to reject <strong>${selectedQuestions.size}</strong> question(s).</p>
        <label class="block text-left text-sm font-semibold text-gray-700 mb-2">Rejection Reason:</label>
        <textarea id="rejectionReason" class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500" rows="3" placeholder="Enter reason for rejection..."></textarea>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, reject',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc2626',
      customClass: { popup: 'rounded-2xl' },
      preConfirm: () => {
        const textarea = document.getElementById('rejectionReason') as HTMLTextAreaElement;
        const reason = textarea?.value.trim();
        if (!reason) {
          Swal.showValidationMessage('Please provide a rejection reason');
          return false;
        }
        return reason;
      },
    });

    if (!result.isConfirmed || !result.value) return;

    try {
      await pdfAnalysisService.batchUpdateQuestionStatus({
        questionIds: Array.from(selectedQuestions),
        status: 'rejected',
        rejectionReason: result.value,
      });

      Swal.fire({
        title: 'Success!',
        text: `${selectedQuestions.size} question(s) have been rejected.`,
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
      });

      setSelectedQuestions(new Set());
      setSelectAll(false);
      fetchQuestions(pagination.page);
    } catch (err: any) {
      Swal.fire({
        title: 'Error',
        text: err.response?.data?.error || 'Failed to reject questions',
        icon: 'error',
        confirmButtonColor: '#dc2626',
      });
    }
  };

  // NQG-RQ-30: run an export with a given filter set + filename, showing a
  // pre-export confirmation with the count. Shared by all three export modes.
  const runExport = async (
    filters: Record<string, any>,
    filenameSuffix: string,
    expectedCount: number
  ) => {
    const confirm = await Swal.fire({
      title: 'Confirm export',
      html: `<p class="text-gray-600">You are about to export <strong>${expectedCount}</strong> question(s) to Excel.</p>`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Export',
      confirmButtonColor: '#1e73be',
      customClass: { popup: 'rounded-2xl' },
    });
    if (!confirm.isConfirmed) return;

    try {
      const blob = await pdfAnalysisService.exportToExcel(filters);
      const now = new Date();
      const date = now.toISOString().split('T')[0];
      const time = now.toTimeString().split(' ')[0].replace(/:/g, '-');
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // AC-5: filename reflects the export criteria
      link.download = `export_${filenameSuffix}_${date}_${time}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      Swal.fire({
        title: 'Export Successful!',
        html: `Exported <strong>${expectedCount} question(s)</strong> to Excel.`,
        icon: 'success',
        confirmButtonColor: '#1e73be',
        customClass: { popup: 'rounded-2xl' },
      });
    } catch (error: any) {
      Swal.fire({
        title: error.response?.status === 404 ? 'No Questions Found' : 'Export Failed',
        text: error.response?.data?.error || 'Could not export questions.',
        icon: 'error',
        confirmButtonColor: '#dc2626',
        customClass: { popup: 'rounded-2xl' },
      });
    }
  };

  // Build the filter object that reflects the currently applied bank filters
  const buildCurrentFilters = () => {
    // Must mirror fetchQuestions() exactly so "export by filter" returns the
    // same set the bank is showing on screen
    const filters: any = {};
    if (difficultyFilter !== 'all') filters.difficulty = difficultyFilter;
    if (typeFilter !== 'all') filters.type = typeFilter;
    if (statusFilter !== 'all') filters.status = statusFilter;
    if (oeCodeFilter) filters.oeCode = oeCodeFilter;
    if (questionIdFilter) filters.questionId = questionIdFilter;
    if (searchTerm) filters.search = searchTerm;
    if (portfolioFilter !== 'all') filters.portfolio = portfolioFilter;
    if (taxonomyNodeFilter) filters.taxonomyNode = taxonomyNodeFilter; // NQG-RQ-31
    if (exportFromDate) filters.fromDate = exportFromDate;
    if (exportToDate) filters.toDate = exportToDate;
    return filters;
  };

  // NQG-RQ-30: selective export — choose All / Selected / By current filter
  const handleSelectiveExport = async () => {
    const hasFilters =
      difficultyFilter !== 'all' || typeFilter !== 'all' || statusFilter !== 'all' ||
      oeCodeFilter !== '' || searchTerm !== '' || !!exportFromDate || !!exportToDate ||
      portfolioFilter !== 'all' || !!taxonomyNodeFilter;
    const selectedCount = selectedQuestions.size;

    const result = await Swal.fire({
      title: 'Export to Excel',
      input: 'radio',
      inputOptions: {
        all: 'Export ALL questions',
        ...(selectedCount > 0 ? { selected: `Export SELECTED (${selectedCount} checked)` } : {}),
        filtered: `Export by current FILTER${hasFilters ? '' : ' (no filters applied — same as all)'}`,
      },
      inputValue: selectedCount > 0 ? 'selected' : 'filtered',
      // NQG-RQ-27 (client request): versions can be exported alongside their
      // originals — each version appears right after its original with a
      // "Version" column (Q1.1, Q1.2...)
      html: `<label style="display:flex;align-items:center;justify-content:center;gap:8px;margin-top:6px;font-size:14px;color:#374151;cursor:pointer">
               <input type="checkbox" id="exportIncludeVersions" checked> Include question versions (Q1.x)
             </label>`,
      showCancelButton: true,
      confirmButtonText: 'Continue',
      confirmButtonColor: '#1e73be',
      customClass: { popup: 'rounded-2xl' },
      // Read BOTH values while the popup is still in the DOM
      preConfirm: () => {
        const radio = (document.querySelector('input[name="swal2-radio"]:checked') as HTMLInputElement)?.value;
        if (!radio) {
          Swal.showValidationMessage('Please choose an export option');
          return false;
        }
        return {
          mode: radio,
          includeVersions:
            (document.getElementById('exportIncludeVersions') as HTMLInputElement)?.checked ?? true,
        };
      },
    });

    if (!result.isConfirmed || !result.value) return;
    const chosenMode = (result.value as any).mode as string;
    const versionParam = (result.value as any).includeVersions ? { includeVersions: 'true' } : {};

    if (chosenMode === 'all') {
      await runExport({ ...versionParam }, 'all', pagination.total);
    } else if (chosenMode === 'selected') {
      if (selectedCount === 0) return;
      await runExport(
        { ids: Array.from(selectedQuestions).join(','), ...versionParam },
        'selected',
        selectedCount
      );
    } else {
      // filtered — count comes from the current paginated result total
      await runExport({ ...buildCurrentFilters(), ...versionParam }, 'filtered', pagination.total);
    }
  };

  // Export ONLY Interactive Questions to Excel - Simple version
  const handleExportInteractiveQuestions = async () => {
    try {
      // Simple: Just export ALL interactive questions, no filters
      const filters: any = {
        type: 'interactive', // Only filter: type = interactive
      };

      // Call the export API
      const blob = await pdfAnalysisService.exportToExcel(filters);

      // Generate timestamp for filename with unique ID
      const now = new Date();
      const date = now.toISOString().split('T')[0];
      const time = now.toTimeString().split(' ')[0].replace(/:/g, '-');
      const uniqueId = Math.random().toString(36).substring(2, 8).toUpperCase();
      const timestamp = `${date}_${time}_${uniqueId}`;

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `interactive-questions-${timestamp}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Show success notification
      Swal.fire({
        title: 'Export Successful!',
        html: `<p class="text-gray-600">Successfully exported <strong>interactive questions</strong> to Excel.</p>`,
        icon: 'success',
        timer: 3000,
        showConfirmButton: false,
        customClass: {
          popup: 'rounded-2xl',
          title: 'text-2xl font-bold text-gray-900',
        },
      });
    } catch (error: any) {
      console.error('Interactive questions Excel export failed:', error);

      const errorMessage = error.response?.data?.error || 'Failed to export interactive questions to Excel.';

      Swal.fire({
        title: error.response?.status === 404 ? 'No Interactive Questions Found' : 'Export Failed',
        html: `<p class="text-gray-600">${errorMessage}</p>`,
        icon: error.response?.status === 404 ? 'warning' : 'error',
        confirmButtonColor: error.response?.status === 404 ? '#f59e0b' : '#dc2626',
        customClass: {
          popup: 'rounded-2xl',
          title: 'text-2xl font-bold text-gray-900',
        },
      });
    }
  };

  // Export ONLY Case Study Questions to Excel
  const handleExportCaseStudyQuestions = async () => {
    try {
      // Simple: Just export ALL case study questions, no filters
      const filters: any = {
        type: 'case_study', // Only filter: type = case_study
      };

      // Call the export API
      const blob = await pdfAnalysisService.exportToExcel(filters);

      // Generate timestamp for filename with unique ID
      const now = new Date();
      const date = now.toISOString().split('T')[0];
      const time = now.toTimeString().split(' ')[0].replace(/:/g, '-');
      const uniqueId = Math.random().toString(36).substring(2, 8).toUpperCase();
      const timestamp = `${date}_${time}_${uniqueId}`;

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `case-study-questions-${timestamp}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Show success notification
      Swal.fire({
        title: 'Export Successful!',
        html: `<p class="text-gray-600">Successfully exported <strong>case study questions</strong> to Excel.</p>`,
        icon: 'success',
        timer: 3000,
        showConfirmButton: false,
        customClass: {
          popup: 'rounded-2xl',
          title: 'text-2xl font-bold text-gray-900',
        },
      });
    } catch (error: any) {
      console.error('Case study questions Excel export failed:', error);

      const errorMessage = error.response?.data?.error || 'Failed to export case study questions to Excel.';

      Swal.fire({
        title: error.response?.status === 404 ? 'No Case Study Questions Found' : 'Export Failed',
        html: `<p class="text-gray-600">${errorMessage}</p>`,
        icon: error.response?.status === 404 ? 'warning' : 'error',
        confirmButtonColor: error.response?.status === 404 ? '#f59e0b' : '#dc2626',
        customClass: {
          popup: 'rounded-2xl',
          title: 'text-2xl font-bold text-gray-900',
        },
      });
    }
  };

  // Sync PDF question bank into the main question bank (NQG-RQ-26 v2)
  const handleSyncToMainBank = async () => {
    const confirmation = await Swal.fire({
      title: 'Sync to Main Bank?',
      text: 'Consolidate all PDF questions into the main question bank?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sync',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#1e73be',
      customClass: { popup: 'rounded-2xl' },
    });
    if (!confirmation.isConfirmed) return;

    setSyncing(true);
    try {
      const result = await freeSourceService.runSync();
      const hasFailures = (result.failed?.length || 0) > 0;
      Swal.fire({
        title: hasFailures ? 'Sync Completed with Errors' : 'Sync Complete',
        html: `
          <div style="text-align:left; font-size:14px; color:#374151;">
            <p><strong>Created:</strong> ${result.created}</p>
            <p><strong>Updated:</strong> ${result.updated}</p>
            <p><strong>Already up to date:</strong> ${result.upToDate}</p>
            <p><strong>Skipped:</strong> ${result.skipped?.length || 0}</p>
            <p><strong>Orphans removed:</strong> ${result.orphansRemoved}</p>
            <p><strong>Failed:</strong> ${result.failed?.length || 0}</p>
          </div>
        `,
        icon: hasFailures ? 'warning' : 'success',
        confirmButtonColor: '#1e73be',
        customClass: { popup: 'rounded-2xl', title: 'text-2xl font-bold text-gray-900' },
      });
    } catch (error: any) {
      if (error?.response?.status === 409) {
        Swal.fire({
          title: 'Sync Already Running',
          text: 'A sync is already in progress. Please wait for it to finish and try again.',
          icon: 'info',
          confirmButtonColor: '#1e73be',
          customClass: { popup: 'rounded-2xl' },
        });
      } else {
        Swal.fire({
          title: 'Sync Failed',
          text: error?.response?.data?.message || error?.message || 'An error occurred during sync.',
          icon: 'error',
          confirmButtonColor: '#dc2626',
          customClass: { popup: 'rounded-2xl' },
        });
      }
    } finally {
      setSyncing(false);
      queryClient.invalidateQueries({ queryKey: ['free-source-sync-status'] });
    }
  };

  // Stats
  const totalQ = pagination.total;
  const approvedQ = questions.filter((q) => q.status === 'approved').length;
  const draftQ = questions.filter((q) => q.status === 'draft' || q.status === 'generated').length;
  const reviewQ = questions.filter((q) => q.status === 'under_review').length;

  // Difficulty distribution
  const easyQ = questions.filter((q) => q.difficulty === 'easy').length;
  const mediumQ = questions.filter((q) => q.difficulty === 'medium').length;
  const hardQ = questions.filter((q) => q.difficulty === 'hard').length;
  const pageTotal = questions.length;

  // OE code groups
  const oeGroups = useMemo(() => {
    const map = new Map<string, number>();
    questions.forEach((q) => {
      const code = q.oeCode || 'No OE';
      map.set(code, (map.get(code) || 0) + 1);
    });
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }, [questions]);

  // Type distribution
  const typeGroups = useMemo(() => {
    const map = new Map<string, number>();
    questions.forEach((q) => {
      map.set(q.type || 'unknown', (map.get(q.type || 'unknown') || 0) + 1);
    });
    return Array.from(map.entries());
  }, [questions]);

  // Columns for SortableTable
  const columns: Column<any>[] = [
    {
      key: 'select',
      label: (
        <input
          type="checkbox"
          checked={selectAll}
          onChange={toggleSelectAll}
          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
        />
      ),
      sortable: false,
      render: (_val, row) => (
        <input
          type="checkbox"
          checked={selectedQuestions.has(row._id)}
          onChange={() => toggleQuestionSelection(row._id)}
          onClick={(e) => e.stopPropagation()}
          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
        />
      ),
    },
    {
      key: '_id',
      label: 'Question ID',
      sortable: true,
      render: (val) => (
        <div className="text-sm font-mono font-medium text-gray-900 break-all">
          {String(val)}
        </div>
      ),
    },
    {
      key: 'stem',
      label: 'Question Preview',
      sortable: true,
      render: (val) => (
        <div className="text-sm text-gray-900 line-clamp-2 max-w-md">{richTextToPlain(val)}</div>
      ),
    },
    {
      key: 'oeCode',
      label: 'OE Code',
      sortable: true,
      render: (val) => (
        <div className="text-sm">
          {val ? (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-blue-100 text-blue-700 border border-blue-300">
              {String(val)}
            </span>
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </div>
      ),
    },
    {
      key: 'type',
      label: 'Type',
      sortable: true,
      render: (val, row) => (
        <div className="flex items-center space-x-2">
          {getTypeIcon(row.type)}
          <span className="text-sm text-gray-900">{getTypeLabel(String(val))}</span>
        </div>
      ),
    },
    {
      key: 'difficulty',
      label: 'Difficulty',
      sortable: true,
      render: (val) => <StatusBadge status={String(val)} />,
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (val) => <StatusBadge status={String(val)} />,
    },
    {
      key: 'cognitiveLevel',
      label: 'Cognitive',
      sortable: true,
      render: (val) => {
        const level = COGNITIVE_LABELS[String(val)];
        return level ? (
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${level.color}`}>
            {level.label}
          </span>
        ) : (
          <span className="text-gray-400">-</span>
        );
      },
    },
    {
      key: 'createdAt',
      label: 'Generated',
      sortable: true,
      render: (_val, row) => (
        <div className="text-xs text-gray-500">
          <div className="flex items-center space-x-1 mb-1">
            <Calendar className="w-3 h-3" />
            <span>{formatDate(row.createdAt)}</span>
          </div>
        </div>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_val, row) => (
        <div className="flex items-center space-x-2">
          <Tooltip content="View details">
            <button
              onClick={() => setDetailQuestion(row)}
              className="text-blue-600 hover:text-blue-900"
            >
              <Eye className="w-4 h-4" />
            </button>
          </Tooltip>
          {canEdit && (
            <Tooltip content="Edit question">
              <button
                onClick={() => navigate(`/pdf-analysis/questions/${row._id}/edit`)}
                className="text-green-600 hover:text-green-900"
              >
                <Edit3 className="w-4 h-4" />
              </button>
            </Tooltip>
          )}
          {canEdit && !row.rootQuestionId && (
            <Tooltip content="Create version">
              <button
                onClick={() => setVersionTarget(row)}
                className="text-purple-600 hover:text-purple-900"
              >
                <GitBranch className="w-4 h-4" />
              </button>
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip content="Delete question">
              <button
                onClick={() => handleDelete(row)}
                className="text-red-600 hover:text-red-900"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </Tooltip>
          )}
          {(row.versionsCount ?? 0) > 0 && (
            <Tooltip content={`View versions (${row.versionsCount})`}>
              <button
                onClick={() => setVersionsViewId(row._id)}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100 transition-colors"
              >
                <GitBranch className="w-3 h-3" />
                {row.versionsCount} version{row.versionsCount > 1 ? 's' : ''}
              </button>
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6 animate-in">
      {/* Header */}
      <div className="flex items-start justify-between">
        <PageHeader
          title="Question Bank"
          description="Manage all generated questions across your PDF analyses"
          breadcrumbs={[
            { label: 'PDF Analysis', path: '/pdf-analysis' },
            { label: 'Question Bank' },
          ]}
        />
        <div className="flex items-center gap-3 mt-10">
          {/* Export Range & Date Filters */}
          <div className="flex flex-col gap-2">
            {/* Question Range */}
            <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
              <span className="text-sm font-medium text-gray-700">Range:</span>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  max={pagination.total}
                  value={exportFromQuestion || ''}
                  onChange={(e) => setExportFromQuestion(parseInt(e.target.value) || 1)}
                  placeholder="From"
                  className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-[#1e73be] focus:border-transparent"
                />
                <span className="text-gray-500">-</span>
                <input
                  type="number"
                  min={exportFromQuestion || 1}
                  max={pagination.total}
                  value={exportToQuestion || ''}
                  onChange={(e) => setExportToQuestion(parseInt(e.target.value) || pagination.total)}
                  placeholder={`To (${pagination.total})`}
                  className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-[#1e73be] focus:border-transparent"
                />
              </div>
            </div>

            {/* Date Range */}
            <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
              <span className="text-sm font-medium text-gray-700">Date:</span>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={exportFromDate}
                  onChange={(e) => setExportFromDate(e.target.value)}
                  className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-[#1e73be] focus:border-transparent"
                />
                <span className="text-gray-500">-</span>
                <input
                  type="date"
                  value={exportToDate}
                  onChange={(e) => setExportToDate(e.target.value)}
                  min={exportFromDate}
                  className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-[#1e73be] focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Batch Actions */}
          {selectedQuestions.size > 0 && (
            <>
              <button
                onClick={handleBatchApprove}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold bg-green-500 hover:bg-green-600 text-white transition-colors shadow-md"
                title={`Approve ${selectedQuestions.size} selected question(s)`}
              >
                <CheckCircle className="w-4 h-4" />
                Approve ({selectedQuestions.size})
              </button>
              <button
                onClick={handleBatchReject}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold bg-red-500 hover:bg-red-600 text-white transition-colors shadow-md"
                title={`Reject ${selectedQuestions.size} selected question(s)`}
              >
                <X className="w-4 h-4" />
                Reject ({selectedQuestions.size})
              </button>
            </>
          )}

          {/* Sync to Main Bank (NQG-RQ-26 v2) */}
          <div className="flex flex-col items-stretch gap-1">
            <button
              onClick={handleSyncToMainBank}
              disabled={syncing || syncStatus?.syncInFlight}
              className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed text-white flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors"
              title={
                syncStatus
                  ? `${syncStatus.mirrored} of ${syncStatus.pdfSupported} synced${
                      syncStatus.lastSyncedAt
                        ? ` · Last sync: ${new Date(syncStatus.lastSyncedAt).toLocaleString()}`
                        : ''
                    }`
                  : 'Consolidate all PDF questions into the main question bank'
              }
            >
              <RefreshCw className={`w-4 h-4 ${syncing || syncStatus?.syncInFlight ? 'animate-spin' : ''}`} />
              {syncing || syncStatus?.syncInFlight ? 'Syncing...' : 'Sync to Main Bank'}
            </button>
            {syncStatus && (
              <span className="text-[10px] text-gray-500 font-medium text-center">
                {syncStatus.mirrored} of {syncStatus.pdfSupported} synced
              </span>
            )}
          </div>

          <button
            onClick={handleSelectiveExport}
            className="btn-primary flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold"
            title="Export all, selected, or filtered questions to Excel"
          >
            <Download className="w-4 h-4" />
            Export Excel{selectedQuestions.size > 0 ? ` (${selectedQuestions.size})` : ''}
          </button>

          <button
            onClick={handleExportInteractiveQuestions}
            className="bg-purple-600 hover:bg-purple-700 text-white flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors"
            title="Export ALL interactive questions from database"
          >
            <Target className="w-4 h-4" />
            Export Interactive
          </button>

          <button
            onClick={handleExportCaseStudyQuestions}
            className="bg-teal-600 hover:bg-teal-700 text-white flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors"
            title="Export ALL case study questions from database"
          >
            <BookOpen className="w-4 h-4" />
            Export Case Study
          </button>

          <button
            onClick={() => fetchQuestions(pagination.page)}
            disabled={loading}
            className="btn-secondary flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => navigate('/pdf-analysis')}
            className="btn-secondary flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        </div>
      </div>

      {/* Hero Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard icon={Database} label="Total" value={totalQ} color="blue" />
        <StatCard icon={CheckCircle} label="Approved" value={approvedQ} color="green" />
        <StatCard icon={FileText} label="Generated" value={draftQ} color="amber" />
        <StatCard icon={Clock} label="In Review" value={reviewQ} color="purple" />
        <StatCard icon={Layers} label="Types" value={typeGroups.length} color="indigo" />
      </div>

      {/* Distribution Row */}
      {false && pageTotal > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Difficulty Distribution */}
          <div className="bg-gradient-to-br from-white via-white to-blue-50/30 rounded-2xl border-2 border-gray-100 shadow-lg hover:shadow-xl transition-shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-gradient-to-br from-[#1e73be] to-[#4A90C8] shadow-md">
                  <BarChart3 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">Difficulty Distribution</h3>
                  <p className="text-xs text-gray-500">Question complexity levels</p>
                </div>
              </div>
              <span className="text-2xl font-bold text-gray-900">{pageTotal}</span>
            </div>

            {/* Progress Bar */}
            <div className="relative h-6 rounded-full overflow-hidden bg-gray-100 mb-4 shadow-inner">
              {easyQ > 0 && (
                <div
                  className="absolute h-full bg-gradient-to-r from-emerald-400 to-emerald-500 transition-all duration-500 ease-out flex items-center justify-center"
                  style={{ width: `${(easyQ / pageTotal) * 100}%`, left: 0 }}
                >
                  {easyQ > 0 && (easyQ / pageTotal) > 0.1 && (
                    <span className="text-xs font-bold text-white">{easyQ}</span>
                  )}
                </div>
              )}
              {mediumQ > 0 && (
                <div
                  className="absolute h-full bg-gradient-to-r from-amber-400 to-amber-500 transition-all duration-500 ease-out flex items-center justify-center"
                  style={{ width: `${(mediumQ / pageTotal) * 100}%`, left: `${(easyQ / pageTotal) * 100}%` }}
                >
                  {mediumQ > 0 && (mediumQ / pageTotal) > 0.1 && (
                    <span className="text-xs font-bold text-white">{mediumQ}</span>
                  )}
                </div>
              )}
              {hardQ > 0 && (
                <div
                  className="absolute h-full bg-gradient-to-r from-red-400 to-red-500 transition-all duration-500 ease-out flex items-center justify-center"
                  style={{ width: `${(hardQ / pageTotal) * 100}%`, left: `${((easyQ + mediumQ) / pageTotal) * 100}%` }}
                >
                  {hardQ > 0 && (hardQ / pageTotal) > 0.1 && (
                    <span className="text-xs font-bold text-white">{hardQ}</span>
                  )}
                </div>
              )}
            </div>

            {/* Legend */}
            <div className="grid grid-cols-3 gap-3">
              <button
                onClick={() => setDifficultyFilter(difficultyFilter === 'easy' ? 'all' : 'easy')}
                className={`p-3 rounded-xl border-2 transition-all hover:scale-105 ${
                  difficultyFilter === 'easy'
                    ? 'bg-emerald-50 border-emerald-300 shadow-md'
                    : 'bg-white border-gray-200 hover:border-emerald-200'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-3 h-3 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-500 shadow-sm" />
                  <span className="text-xs font-bold text-emerald-700">Easy</span>
                </div>
                <div className="text-left">
                  <span className="text-xl font-bold text-gray-900">{easyQ}</span>
                  <span className="text-xs text-gray-500 ml-1">({((easyQ / pageTotal) * 100).toFixed(0)}%)</span>
                </div>
              </button>

              <button
                onClick={() => setDifficultyFilter(difficultyFilter === 'medium' ? 'all' : 'medium')}
                className={`p-3 rounded-xl border-2 transition-all hover:scale-105 ${
                  difficultyFilter === 'medium'
                    ? 'bg-amber-50 border-amber-300 shadow-md'
                    : 'bg-white border-gray-200 hover:border-amber-200'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-3 h-3 rounded-full bg-gradient-to-br from-amber-400 to-amber-500 shadow-sm" />
                  <span className="text-xs font-bold text-amber-700">Medium</span>
                </div>
                <div className="text-left">
                  <span className="text-xl font-bold text-gray-900">{mediumQ}</span>
                  <span className="text-xs text-gray-500 ml-1">({((mediumQ / pageTotal) * 100).toFixed(0)}%)</span>
                </div>
              </button>

              <button
                onClick={() => setDifficultyFilter(difficultyFilter === 'hard' ? 'all' : 'hard')}
                className={`p-3 rounded-xl border-2 transition-all hover:scale-105 ${
                  difficultyFilter === 'hard'
                    ? 'bg-red-50 border-red-300 shadow-md'
                    : 'bg-white border-gray-200 hover:border-red-200'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-3 h-3 rounded-full bg-gradient-to-br from-red-400 to-red-500 shadow-sm" />
                  <span className="text-xs font-bold text-red-700">Hard</span>
                </div>
                <div className="text-left">
                  <span className="text-xl font-bold text-gray-900">{hardQ}</span>
                  <span className="text-xs text-gray-500 ml-1">({((hardQ / pageTotal) * 100).toFixed(0)}%)</span>
                </div>
              </button>
            </div>
          </div>

          {/* Type Distribution & OE Codes */}
          <div className="bg-gradient-to-br from-white via-white to-orange-50/30 rounded-2xl border-2 border-gray-100 shadow-lg hover:shadow-xl transition-shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-gradient-to-br from-[#ea7500] to-[#ff8c1a] shadow-md">
                  <Layers className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">Type Distribution</h3>
                  <p className="text-xs text-gray-500">{typeGroups.length} question types • {oeGroups.length} OE codes</p>
                </div>
              </div>
            </div>

            {/* Type Badges */}
            <div className="flex flex-wrap gap-2 mb-4 pb-4 border-b border-gray-200">
              {typeGroups.map(([type, count]) => {
                const tc = TYPE_CONFIG[type] || TYPE_CONFIG.multiple_choice;
                const TypeIcon = tc.icon;
                const isSelected = typeFilter === type;
                const percentage = ((count / pageTotal) * 100).toFixed(0);

                return (
                  <button
                    key={type}
                    onClick={() => setTypeFilter(isSelected ? 'all' : type)}
                    className={`group relative flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 transition-all hover:scale-105 ${
                      isSelected
                        ? `${tc.bg} ${tc.border} shadow-md`
                        : 'bg-white border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <TypeIcon className={`w-4 h-4 ${isSelected ? tc.color : 'text-gray-400'} transition-colors`} />
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-bold ${isSelected ? tc.color : 'text-gray-700'}`}>{tc.label}</span>
                      <div className="flex flex-col items-center">
                        <span className="text-base font-bold text-gray-900">{count}</span>
                        <span className="text-[10px] text-gray-500 -mt-1">{percentage}%</span>
                      </div>
                    </div>
                    {isSelected && (
                      <span className="absolute -top-1 -right-1 w-2 h-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full shadow-sm" />
                    )}
                  </button>
                );
              })}
            </div>

            {/* OE Codes */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider">OE Codes</h4>
                {oeCodeFilter && (
                  <button
                    onClick={() => setOeCodeFilter('')}
                    className="text-xs text-red-500 hover:text-red-700 font-semibold flex items-center gap-1"
                  >
                    <X className="w-3 h-3" />
                    Clear
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto custom-scrollbar pr-2">
                {oeGroups.map(([code, count]) => {
                  const isSelected = oeCodeFilter === code;
                  return (
                    <button
                      key={code}
                      onClick={() => setOeCodeFilter(isSelected ? '' : code)}
                      className={`group relative px-3 py-1.5 rounded-lg border-2 transition-all hover:scale-105 ${
                        isSelected
                          ? 'bg-blue-50 border-[#1e73be] shadow-md'
                          : 'bg-white border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      <div className="flex items-center gap-1.5">
                        <span className={`text-xs font-mono font-bold ${isSelected ? 'text-[#1e73be]' : 'text-gray-700'}`}>
                          {code}
                        </span>
                        <span className={`text-xs font-semibold ${isSelected ? 'text-[#1e73be]' : 'text-gray-400'}`}>
                          {count}
                        </span>
                      </div>
                      {isSelected && (
                        <span className="absolute -top-1 -right-1 w-2 h-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full shadow-sm" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search + Filters */}
      <div className="bg-gradient-to-br from-white to-gray-50/50 rounded-2xl border-2 border-gray-100 shadow-lg hover:shadow-xl transition-shadow p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search content — stem, alternatives, explanations, OE codes, keywords..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-12 py-3.5 text-sm border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1e73be] focus:border-[#1e73be] transition-all shadow-sm hover:border-gray-300"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`relative flex items-center gap-2 px-5 py-3.5 rounded-xl text-sm font-bold border-2 transition-all duration-200 hover:scale-105 shadow-sm ${
              showFilters || hasActiveFilters
                ? 'border-[#1e73be] bg-gradient-to-r from-blue-50 to-blue-100 text-[#1e73be] shadow-md'
                : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
            }`}
          >
            <Filter className="w-4 h-4" />
            <span>Filters</span>
            {hasActiveFilters && (
              <span className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 rounded-full bg-gradient-to-br from-[#ea7500] to-[#ff8c1a] text-white text-[10px] font-bold shadow-md">
                {[difficultyFilter !== 'all', typeFilter !== 'all', statusFilter !== 'all', oeCodeFilter !== '', questionIdFilter !== '', portfolioFilter !== 'all'].filter(Boolean).length}
              </span>
            )}
          </button>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-bold text-red-500 hover:text-white hover:bg-red-500 border-2 border-red-200 hover:border-red-500 transition-all hover:scale-105 shadow-sm"
            >
              <X className="w-4 h-4" />
              Clear
            </button>
          )}
        </div>

        {showFilters && (
          <div className="space-y-4 pt-4 border-t-2 border-gray-100">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Difficulty Filter */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-xs font-bold text-gray-700 uppercase tracking-wider">
                  <BarChart3 className="w-3.5 h-3.5 text-[#1e73be]" />
                  Difficulty
                </label>
                <select
                  value={difficultyFilter}
                  onChange={(e) => setDifficultyFilter(e.target.value)}
                  className="w-full px-4 py-3 text-sm font-medium border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1e73be] focus:border-[#1e73be] transition-all shadow-sm hover:border-gray-300 bg-white"
                >
                  <option value="all">All Difficulties</option>
                  {DIFFICULTIES.map((d) => (
                    <option key={d} value={d}>
                      {d.charAt(0).toUpperCase() + d.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Type Filter */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-xs font-bold text-gray-700 uppercase tracking-wider">
                  <Layers className="w-3.5 h-3.5 text-[#ea7500]" />
                  Question Type
                </label>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="w-full px-4 py-3 text-sm font-medium border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1e73be] focus:border-[#1e73be] transition-all shadow-sm hover:border-gray-300 bg-white"
                >
                  <option value="all">All Types</option>
                  {TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Status Filter */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-xs font-bold text-gray-700 uppercase tracking-wider">
                  <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                  Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-4 py-3 text-sm font-medium border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1e73be] focus:border-[#1e73be] transition-all shadow-sm hover:border-gray-300 bg-white"
                >
                  <option value="all">All Statuses</option>
                  {STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Question ID + Portfolio Filter - Second Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Portfolio Filter (NQG-RQ-29) */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-xs font-bold text-gray-700 uppercase tracking-wider">
                  <Database className="w-3.5 h-3.5 text-[#1e73be]" />
                  Portfolio
                </label>
                <select
                  value={portfolioFilter}
                  onChange={(e) => setPortfolioFilter(e.target.value)}
                  className="w-full px-4 py-3 text-sm font-medium border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1e73be] focus:border-[#1e73be] transition-all shadow-sm hover:border-gray-300 bg-white"
                >
                  <option value="all">All portfolios</option>
                  {portfolios.map((p) => (
                    <option key={p._id} value={p.name}>
                      {p.name}
                    </option>
                  ))}
                </select>
                {/* NQG-RQ-31: drill down Theme → Subtheme → Sub-subtheme */}
                {selectedPortfolioId && (
                  <TaxonomyCascade
                    portfolioId={selectedPortfolioId}
                    value={taxonomyNodeFilter}
                    onChange={(nodeId) => setTaxonomyNodeFilter(nodeId)}
                    compact
                  />
                )}
              </div>

              {/* Question ID Filter */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-xs font-bold text-gray-700 uppercase tracking-wider">
                  <Hash className="w-3.5 h-3.5 text-purple-500" />
                  Question ID
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Enter question ID..."
                    value={questionIdFilter}
                    onChange={(e) => setQuestionIdFilter(e.target.value)}
                    className="w-full px-4 py-3 text-sm font-mono font-medium border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1e73be] focus:border-[#1e73be] transition-all shadow-sm hover:border-gray-300 bg-white"
                  />
                  {questionIdFilter && (
                    <button
                      onClick={() => setQuestionIdFilter('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Active Filters Display */}
            {hasActiveFilters && (
              <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Active:</span>
                <div className="flex flex-wrap gap-2">
                  {difficultyFilter !== 'all' && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200 text-xs font-semibold text-emerald-700">
                      <BarChart3 className="w-3 h-3" />
                      {difficultyFilter.charAt(0).toUpperCase() + difficultyFilter.slice(1)}
                      <button onClick={() => setDifficultyFilter('all')} className="ml-1 hover:text-red-600">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  {typeFilter !== 'all' && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-200 text-xs font-semibold text-blue-700">
                      <Layers className="w-3 h-3" />
                      {TYPES.find(t => t.value === typeFilter)?.label}
                      <button onClick={() => setTypeFilter('all')} className="ml-1 hover:text-red-600">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  {statusFilter !== 'all' && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-50 border border-purple-200 text-xs font-semibold text-purple-700">
                      <CheckCircle className="w-3 h-3" />
                      {STATUSES.find(s => s.value === statusFilter)?.label}
                      <button onClick={() => setStatusFilter('all')} className="ml-1 hover:text-red-600">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  {questionIdFilter !== '' && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-200 text-xs font-semibold text-gray-700">
                      <Hash className="w-3 h-3" />
                      ID: <span className="font-bold">{questionIdFilter}</span>
                      <button onClick={() => setQuestionIdFilter('')} className="ml-1 hover:text-red-600">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {totalQ > 0 && (
        <div className="bg-gradient-to-br from-white to-gray-50/50 rounded-2xl border-2 border-gray-100 shadow-lg p-5">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Results Info + Items Per Page */}
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-xl border-2 border-blue-200">
                <Database className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-bold text-blue-900">
                  {((pagination.page - 1) * pagination.limit) + 1}-{Math.min(pagination.page * pagination.limit, totalQ)}
                </span>
                <span className="text-sm text-blue-600">of</span>
                <span className="text-sm font-bold text-blue-900">{totalQ}</span>
              </div>
              <span className="text-sm text-gray-600">questions</span>

              {/* View Toggle */}
              <ViewToggle view={view} onViewChange={setView} />

              {/* Items Per Page Selector */}
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-600">Show:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                  className="px-3 py-2 text-sm font-bold border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1e73be] focus:border-[#1e73be] transition-all hover:border-gray-300 bg-white shadow-sm"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-xs font-medium text-gray-600">per page</span>
              </div>
            </div>

            {/* Pagination Controls */}
            {pagination.pages > 1 && (
              <div className="flex items-center gap-2">
                {/* First Page */}
                <button
                  onClick={() => fetchQuestions(1)}
                  disabled={pagination.page === 1 || loading}
                  className="p-2.5 rounded-xl border-2 border-gray-200 text-gray-600 hover:bg-blue-50 hover:border-[#1e73be] hover:text-[#1e73be] disabled:opacity-40 disabled:cursor-not-allowed transition-all hover:scale-110 bg-white shadow-sm"
                  title="First Page"
                >
                  <ChevronDown className="w-4 h-4 rotate-90" />
                </button>

                {/* Previous Page */}
                <button
                  onClick={() => fetchQuestions(pagination.page - 1)}
                  disabled={pagination.page <= 1 || loading}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-gray-200 text-sm font-bold text-gray-600 hover:bg-blue-50 hover:border-[#1e73be] hover:text-[#1e73be] disabled:opacity-40 disabled:cursor-not-allowed transition-all hover:scale-105 bg-white shadow-sm"
                >
                  <ChevronDown className="w-4 h-4 rotate-90" />
                  Previous
                </button>

                {/* Page Numbers */}
                <div className="hidden md:flex items-center gap-2">
                  {[...Array(pagination.pages)].map((_, i) => {
                    const pageNum = i + 1;
                    // Show first page, last page, current page, and pages around current
                    const showPage =
                      pageNum === 1 ||
                      pageNum === pagination.pages ||
                      (pageNum >= pagination.page - 1 && pageNum <= pagination.page + 1);

                    // Show ellipsis
                    const showEllipsisBefore = pageNum === pagination.page - 2 && pagination.page > 3;
                    const showEllipsisAfter = pageNum === pagination.page + 2 && pagination.page < pagination.pages - 2;

                    if (showEllipsisBefore || showEllipsisAfter) {
                      return (
                        <span key={i} className="px-2 text-gray-400">
                          ...
                        </span>
                      );
                    }

                    if (!showPage) return null;

                    return (
                      <button
                        key={i}
                        onClick={() => fetchQuestions(pageNum)}
                        disabled={loading}
                        className={`min-w-[40px] h-10 rounded-xl border-2 text-sm font-bold transition-all hover:scale-110 shadow-sm ${
                          pagination.page === pageNum
                            ? 'bg-gradient-to-r from-[#1e73be] to-[#4A90C8] border-[#1e73be] text-white shadow-lg'
                            : 'bg-white border-gray-200 text-gray-600 hover:bg-blue-50 hover:border-[#1e73be] hover:text-[#1e73be]'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                {/* Mobile: Current Page Indicator */}
                <div className="md:hidden flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-[#1e73be] to-[#4A90C8] rounded-xl border-2 border-[#1e73be] shadow-lg">
                  <span className="text-sm font-bold text-white">
                    {pagination.page} / {pagination.pages}
                  </span>
                </div>

                {/* Next Page */}
                <button
                  onClick={() => fetchQuestions(pagination.page + 1)}
                  disabled={pagination.page >= pagination.pages || loading}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-gray-200 text-sm font-bold text-gray-600 hover:bg-blue-50 hover:border-[#1e73be] hover:text-[#1e73be] disabled:opacity-40 disabled:cursor-not-allowed transition-all hover:scale-105 bg-white shadow-sm"
                >
                  Next
                  <ChevronDown className="w-4 h-4 -rotate-90" />
                </button>

                {/* Last Page */}
                <button
                  onClick={() => fetchQuestions(pagination.pages)}
                  disabled={pagination.page === pagination.pages || loading}
                  className="p-2.5 rounded-xl border-2 border-gray-200 text-gray-600 hover:bg-blue-50 hover:border-[#1e73be] hover:text-[#1e73be] disabled:opacity-40 disabled:cursor-not-allowed transition-all hover:scale-110 bg-white shadow-sm"
                  title="Last Page"
                >
                  <ChevronDown className="w-4 h-4 -rotate-90" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 rounded-2xl border border-red-200 p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-red-800">Failed to load questions</p>
            <p className="text-xs text-red-600 mt-0.5">{error}</p>
          </div>
          <button
            onClick={() => fetchQuestions(1)}
            className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="flex items-center gap-3 text-gray-500">
            <Loader2 className="w-6 h-6 animate-spin text-[#1e73be]" />
            <span className="text-sm font-medium">Loading questions...</span>
          </div>
        </div>
      )}

      {/* Questions */}
      {!loading && questions.length === 0 && !error ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
          <EmptyState
            icon={Database}
            title="No questions found"
            description={
              hasActiveFilters
                ? 'No questions match your current filters.'
                : 'No questions have been generated yet. Go to a PDF analysis and generate some.'
            }
            action={hasActiveFilters ? { label: 'Clear Filters', onClick: clearFilters } : undefined}
          />
        </div>
      ) : !loading && view === 'list' ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <SortableTable
            columns={columns}
            data={questions}
            keyExtractor={(q) => q._id}
            emptyMessage="No questions to display"
          />
        </div>
      ) : !loading && (
        <div className="space-y-3">
          {/* Select All Header */}
          {questions.length > 0 && (
            <div className="bg-white rounded-xl border-2 border-gray-200 px-4 py-3 flex items-center gap-3">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={toggleSelectAll}
                className="w-5 h-5 rounded border-gray-300 text-[#1e73be] focus:ring-[#1e73be] cursor-pointer"
              />
              <span className="text-sm font-semibold text-gray-700">
                {selectedQuestions.size > 0
                  ? `${selectedQuestions.size} question(s) selected`
                  : 'Select all questions on this page'}
              </span>
              {selectedQuestions.size > 0 && (
                <button
                  onClick={() => {
                    setSelectedQuestions(new Set());
                    setSelectAll(false);
                  }}
                  className="ml-auto text-sm text-red-500 hover:text-red-700 font-semibold flex items-center gap-1"
                >
                  <X className="w-4 h-4" />
                  Clear Selection
                </button>
              )}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {questions.map((question) => (
            <div
              key={question._id}
              className={`bg-white rounded-2xl border p-5 hover:shadow-lg transition-all duration-300 ${
                selectedQuestions.has(question._id)
                  ? 'border-blue-500 ring-2 ring-blue-200'
                  : 'border-gray-200 hover:border-blue-300'
              }`}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3 flex-1">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    {getTypeIcon(question.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xs font-mono text-gray-500 break-all">
                      {question._id}
                    </h3>
                    {question.oeCode && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-blue-100 text-blue-700 border border-blue-300 mt-1">
                        {question.oeCode}
                      </span>
                    )}
                    {/* NQG-RQ-29: portfolio tag(s) */}
                    {(question.portfolios || []).map((pf: string) => (
                      <span key={pf} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-purple-100 text-purple-700 border border-purple-300 mt-1 ml-1">
                        {pf}
                      </span>
                    ))}
                  </div>
                </div>
                {/* Checkbox */}
                <input
                  type="checkbox"
                  checked={selectedQuestions.has(question._id)}
                  onChange={() => toggleQuestionSelection(question._id)}
                  onClick={(e) => e.stopPropagation()}
                  className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer flex-shrink-0"
                />
              </div>

              {/* Question Text */}
              <p className="text-sm text-gray-900 mb-3 line-clamp-3">{richTextToPlain(question.stem) || 'No question text'}</p>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-3">
                <StatusBadge status={question.difficulty} />
                <StatusBadge status={question.status} />
                <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full border border-gray-200">
                  {getTypeLabel(question.type)}
                </span>
                {question.cognitiveLevel && COGNITIVE_LABELS[question.cognitiveLevel] && (
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${COGNITIVE_LABELS[question.cognitiveLevel].color}`}>
                    {COGNITIVE_LABELS[question.cognitiveLevel].label}
                  </span>
                )}
                {(question.versionsCount ?? 0) > 0 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setVersionsViewId(question._id);
                    }}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-bold rounded-full bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100 transition-colors"
                    title="View versions"
                  >
                    <GitBranch className="w-3 h-3" />
                    {question.versionsCount} version{question.versionsCount > 1 ? 's' : ''}
                  </button>
                )}
              </div>

              {/* Details */}
              <div className="space-y-2 mb-4 text-xs">
                {question.correctAlternative && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Correct Answer:</span>
                    <span className="font-bold text-green-700 bg-green-50 px-2 py-0.5 rounded">
                      {question.correctAlternative}
                    </span>
                  </div>
                )}
                {question.createdAt && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Created:</span>
                    <span className="font-medium text-gray-900">
                      {formatDate(question.createdAt)}
                    </span>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="pt-3 border-t border-gray-200">
                <div className="flex items-center space-x-2">
                  <Tooltip content="View full details">
                    <button
                      onClick={() => setDetailQuestion(question)}
                      className="flex-1 px-3 py-2 text-xs font-semibold rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors flex items-center justify-center"
                    >
                      <Eye className="w-3 h-3 mr-1" />
                      View
                    </button>
                  </Tooltip>
                  {canEdit && (
                    <Tooltip content="Edit question">
                      <button
                        onClick={() => navigate(`/pdf-analysis/questions/${question._id}/edit`)}
                        className="flex-1 px-3 py-2 text-xs font-semibold rounded-lg bg-green-50 text-green-600 hover:bg-green-100 transition-colors flex items-center justify-center"
                      >
                        <Edit3 className="w-3 h-3 mr-1" />
                        Edit
                      </button>
                    </Tooltip>
                  )}
                  {canEdit && !question.rootQuestionId && (
                    <Tooltip content="Create version">
                      <button
                        onClick={() => setVersionTarget(question)}
                        className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                      >
                        <GitBranch className="w-4 h-4" />
                      </button>
                    </Tooltip>
                  )}
                  {canDelete && (
                    <Tooltip content="Delete question">
                      <button
                        onClick={() => handleDelete(question)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </Tooltip>
                  )}
                </div>
              </div>
            </div>
          ))}
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {detailQuestion && (
        <QuestionDetailModal
          question={detailQuestion}
          onClose={() => setDetailQuestion(null)}
          onEdit={() => {
            navigate(`/pdf-analysis/questions/${detailQuestion._id}/edit`);
            setDetailQuestion(null);
          }}
          canEdit={canEdit}
          onOpenVersions={() => {
            const qid = detailQuestion._id;
            setDetailQuestion(null);
            setVersionsViewId(qid);
          }}
        />
      )}

      {/* Create Version Modal (NQG-RQ-27) */}
      {versionTarget && (
        <CreateVersionModal
          question={versionTarget}
          onClose={() => setVersionTarget(null)}
          onCreated={() => {
            const rootId = versionTarget._id;
            setVersionTarget(null);
            fetchQuestions(pagination.page); // refresh list (versionsCount badge)
            setVersionsViewId(rootId); // open the versions view for the question
          }}
        />
      )}

      {/* Version History Modal (NQG-RQ-27) */}
      {versionsViewId && (
        <VersionHistoryModal
          questionId={versionsViewId}
          canEdit={canEdit}
          onClose={() => setVersionsViewId(null)}
          onView={(doc) => {
            setVersionsViewId(null);
            setDetailQuestion(doc);
          }}
        />
      )}
    </div>
  );
}

// ── Stat Card ───────────────────────────────────────────────────────────────

function StatCard({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: React.ElementType;
  label: string;
  value: number | string;
  color: string;
}) {
  const cm: Record<string, { bg: string; ic: string; tx: string; border: string }> = {
    blue:   { bg: 'from-blue-50 to-blue-100/50',    ic: 'bg-blue-100 text-blue-600',    tx: 'text-blue-700', border: 'border-blue-300' },
    green:  { bg: 'from-emerald-50 to-green-100/50', ic: 'bg-green-100 text-green-600',  tx: 'text-green-700', border: 'border-green-300' },
    amber:  { bg: 'from-amber-50 to-orange-100/50',  ic: 'bg-amber-100 text-amber-600',  tx: 'text-amber-700', border: 'border-amber-300' },
    purple: { bg: 'from-purple-50 to-violet-100/50',  ic: 'bg-purple-100 text-purple-600', tx: 'text-purple-700', border: 'border-purple-300' },
    indigo: { bg: 'from-indigo-50 to-indigo-100/50',  ic: 'bg-indigo-100 text-indigo-600', tx: 'text-indigo-700', border: 'border-indigo-300' },
  };
  const c = cm[color] || cm.blue;

  return (
    <div className={`bg-gradient-to-br ${c.bg} rounded-2xl border-2 ${c.border} p-4 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 hover:scale-105`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-bold text-gray-600 uppercase tracking-wide">{label}</p>
          <p className={`text-3xl font-black mt-1 ${c.tx}`}>{value}</p>
        </div>
        <div className={`p-3 rounded-xl ${c.ic} shadow-md`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
}

// ── Helper: Format seconds to readable time ─────────────────────────────────

function formatTime(seconds: number): string {
  if (!seconds) return '';
  if (seconds < 60) return `${seconds}s`;
  const min = Math.floor(seconds / 60);
  const sec = seconds % 60;
  return sec > 0 ? `${min}m ${sec}s` : `${min}m`;
}

// ── Helper: Render case data as table ───────────────────────────────────────

function CaseDataTable({ data }: { data: any }) {
  if (!data || typeof data !== 'object') return null;
  const entries = Object.entries(data);
  if (entries.length === 0) return null;

  // Check if it's a nested object (table-like data)
  const isTableData = entries.some(([, v]) => typeof v === 'object' && v !== null && !Array.isArray(v));

  if (isTableData) {
    // Render as a structured table
    const columns = new Set<string>();
    const rows: Array<{ label: string; values: Record<string, any> }> = [];
    entries.forEach(([key, val]) => {
      if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
        Object.keys(val as object).forEach((k) => columns.add(k));
        rows.push({ label: key, values: val as Record<string, any> });
      }
    });
    const colArr = Array.from(columns);

    if (rows.length > 0 && colArr.length > 0) {
      return (
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-purple-100/50">
                <th className="text-left px-3 py-2 font-bold text-purple-800 border-b border-purple-200">Item</th>
                {colArr.map((col) => (
                  <th key={col} className="text-left px-3 py-2 font-bold text-purple-800 border-b border-purple-200 capitalize">
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-purple-50/30'}>
                  <td className="px-3 py-1.5 font-semibold text-gray-700 border-b border-gray-100">{row.label}</td>
                  {colArr.map((col) => (
                    <td key={col} className="px-3 py-1.5 text-gray-600 border-b border-gray-100">
                      {row.values[col] != null ? String(row.values[col]) : '-'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
  }

  // Flat key-value display
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
      {entries.map(([key, val]) => (
        <div key={key} className="bg-purple-50/50 rounded-lg px-3 py-2">
          <p className="text-[10px] font-semibold text-purple-600 uppercase">{key}</p>
          <p className="text-xs text-gray-800 font-medium">{typeof val === 'object' ? JSON.stringify(val) : String(val)}</p>
        </div>
      ))}
    </div>
  );
}

// ── Helper: Metadata chips ──────────────────────────────────────────────────

function MetadataSection({ q }: { q: any }) {
  const chips: Array<{ icon: React.ElementType; label: string; value: string; color: string }> = [];

  if (q.cognitiveLevel) {
    const cl = COGNITIVE_LABELS[q.cognitiveLevel];
    chips.push({ icon: Brain, label: 'Cognitive', value: cl?.label || q.cognitiveLevel, color: cl?.color || 'text-gray-600 bg-gray-50' });
  }
  if (q.estimatedTime) {
    chips.push({ icon: Timer, label: 'Time', value: formatTime(q.estimatedTime), color: 'text-gray-600 bg-gray-50' });
  }
  if (q.hasCalculation) {
    chips.push({ icon: TrendingUp, label: 'Type', value: 'Calculation', color: 'text-amber-600 bg-amber-50' });
  }
  if (q.mcqFormat) {
    chips.push({ icon: Target, label: 'Format', value: q.mcqFormat, color: 'text-indigo-600 bg-indigo-50' });
  }
  if (q.validationStatus) {
    chips.push({ icon: Award, label: 'Validation', value: q.validationStatus, color: 'text-gray-600 bg-gray-50' });
  }
  if (q.generatedBy) {
    chips.push({ icon: Zap, label: 'By', value: q.generatedBy, color: 'text-gray-600 bg-gray-50' });
  }

  const hasKeywords = q.keywords?.length > 0;
  const hasTopics = q.topics?.length > 0;
  const hasTags = q.tags?.length > 0;
  const hasBehavioral = q.behavioralSkills?.length > 0;

  if (chips.length === 0 && !hasKeywords && !hasTopics && !hasTags && !hasBehavioral) return null;

  return (
    <div className="space-y-3 pt-2">
      {/* Chips row */}
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {chips.map((chip, i) => {
            const ChipIcon = chip.icon;
            return (
              <div key={i} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg ${chip.color} text-xs font-medium`}>
                <ChipIcon className="w-3 h-3" />
                <span>{chip.value}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Behavioral Skills */}
      {hasBehavioral && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-bold text-gray-400 uppercase">Skills:</span>
          {q.behavioralSkills.map((bs: any, i: number) => (
            <div key={i} className="flex items-center gap-1 px-2 py-0.5 rounded bg-violet-50 text-violet-700 text-xs">
              <Star className="w-3 h-3" />
              <span className="font-medium">{bs.skill}</span>
              <span className="text-[10px] font-bold text-violet-500">L{bs.level}</span>
            </div>
          ))}
        </div>
      )}

      {/* Topics */}
      {hasTopics && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-bold text-gray-400 uppercase">Topics:</span>
          {q.topics.map((t: any, i: number) => (
            <span key={i} className="px-2 py-0.5 rounded bg-sky-50 text-sky-700 text-xs font-medium">
              {t.title || t}
            </span>
          ))}
        </div>
      )}

      {/* Keywords */}
      {hasKeywords && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-bold text-gray-400 uppercase">Keywords:</span>
          {q.keywords.map((k: string, i: number) => (
            <span key={i} className="px-2 py-0.5 rounded bg-gray-100 text-gray-600 text-xs font-medium flex items-center gap-1">
              <Hash className="w-2.5 h-2.5" />{k}
            </span>
          ))}
        </div>
      )}

      {/* Tags */}
      {hasTags && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-bold text-gray-400 uppercase">Tags:</span>
          {q.tags.map((t: string, i: number) => (
            <span key={i} className="px-2 py-0.5 rounded bg-teal-50 text-teal-700 text-xs font-medium flex items-center gap-1">
              <Tag className="w-2.5 h-2.5" />{t}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Question Row ────────────────────────────────────────────────────────────

// @ts-expect-error - Unused component kept for future reference
function QuestionRow({
  question: q,
  isExpanded,
  isSelected,
  onToggle,
  onSelect,
  onView,
  onEdit,
  onFlag,
  onDelete,
}: {
  question: any;
  isExpanded: boolean;
  isSelected: boolean;
  onToggle: () => void;
  onSelect: () => void;
  onView: () => void;
  onEdit: () => void;
  onFlag: () => void;
  onDelete: () => void;
}) {
  const tc = TYPE_CONFIG[q.type] || TYPE_CONFIG.multiple_choice;
  const TypeIcon = tc.icon;

  // Difficulty color mapping
  const difficultyStyles = {
    easy: 'bg-gradient-to-r from-emerald-50 to-green-50 border-emerald-200 text-emerald-700',
    medium: 'bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200 text-amber-700',
    hard: 'bg-gradient-to-r from-red-50 to-rose-50 border-red-200 text-red-700',
  };

  return (
    <div className={`group bg-gradient-to-br from-white to-gray-50/50 rounded-2xl border-2 overflow-hidden transition-all duration-300 hover:shadow-xl ${
      isExpanded
        ? 'border-[#1e73be] shadow-2xl scale-[1.01]'
        : isSelected
        ? 'border-blue-400 shadow-lg bg-blue-50/30'
        : 'border-gray-200 shadow-md hover:border-[#1e73be]/30'
    }`}>
      {/* Collapsed row */}
      <div className="flex items-center gap-4 p-5 cursor-pointer" onClick={onToggle}>
        {/* Selection Checkbox */}
        <div onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onSelect}
            className="w-5 h-5 rounded border-gray-300 text-[#1e73be] focus:ring-[#1e73be] cursor-pointer"
          />
        </div>

        {/* Type icon with gradient background */}
        <div className={`relative flex-shrink-0 w-14 h-14 rounded-2xl ${tc.bg} border-2 ${tc.border} flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300`}>
          <TypeIcon className={`w-7 h-7 ${tc.color}`} />
          {q.validationStatus === 'ai_wrong' && (
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center shadow-lg">
              <ShieldAlert className="w-2.5 h-2.5 text-white" />
            </span>
          )}
        </div>

        <div className="flex-1 min-w-0 space-y-2">
          {/* Badges Row */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* Question ID Badge */}
            {q._id && (
              <span className="inline-flex items-center gap-1.5 font-mono text-[10px] font-semibold text-gray-500 bg-gray-50 px-2.5 py-1 rounded border border-gray-200">
                ID: {q._id}
              </span>
            )}

            {q.oeCode && (
              <span className="inline-flex items-center gap-1.5 font-mono text-xs font-bold text-[#1e73be] bg-gradient-to-r from-blue-50 to-blue-100 px-3 py-1.5 rounded-lg border-2 border-blue-200 shadow-sm">
                <Hash className="w-3 h-3" />
                {q.oeCode}
              </span>
            )}

            {/* NQG-RQ-29: portfolio tag(s) on the question (client feedback) */}
            {(q.portfolios || []).map((pf: string) => (
              <span key={pf} className="inline-flex items-center gap-1 text-xs font-bold text-purple-700 bg-purple-50 px-3 py-1.5 rounded-lg border-2 border-purple-200 shadow-sm">
                <Database className="w-3 h-3" />
                {pf}
              </span>
            ))}

            <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg border-2 shadow-sm ${difficultyStyles[q.difficulty as keyof typeof difficultyStyles] || difficultyStyles.medium}`}>
              <TrendingUp className="w-3 h-3" />
              {q.difficulty}
            </span>

            <span className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase px-3 py-1.5 rounded-lg border-2 ${tc.bg} ${tc.border} ${tc.color} shadow-sm`}>
              <TypeIcon className="w-3 h-3" />
              {tc.label}
            </span>

            <StatusBadge status={q.status || 'draft'} />

            {q.hasCalculation && (
              <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-600 bg-amber-50 px-3 py-1.5 rounded-lg border-2 border-amber-200 shadow-sm">
                <Target className="w-3 h-3" />
                Calc
              </span>
            )}

            {q.cognitiveLevel && (
              <span className={`inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-lg border-2 shadow-sm ${COGNITIVE_LABELS[q.cognitiveLevel]?.color || 'text-gray-600 bg-gray-50 border-gray-200'}`}>
                <Brain className="w-3 h-3" />
                {COGNITIVE_LABELS[q.cognitiveLevel]?.label || q.cognitiveLevel}
              </span>
            )}
          </div>

          {/* Question Text */}
          <p className="text-sm text-gray-800 leading-relaxed line-clamp-2 font-medium">
            {q.type === 'case_study' && q.caseTitle ? (
              <>
                <span className="inline-flex items-center gap-1 font-bold text-purple-700 bg-purple-50 px-2 py-0.5 rounded">
                  <BookOpen className="w-3 h-3" />
                  {q.caseTitle}
                </span>
                {' '}{richTextToPlain(q.stem)}
              </>
            ) : q.type === 'interactive' && q.initialContext ? (
              <>
                <span className="inline-flex items-center gap-1 font-bold text-orange-700 bg-orange-50 px-2 py-0.5 rounded">
                  <MessageSquare className="w-3 h-3" />
                  Interactive
                </span>
                {' '}{richTextToPlain(q.initialContext).substring(0, 150)}{richTextToPlain(q.initialContext).length > 150 ? '...' : ''}
              </>
            ) : (
              richTextToPlain(q.stem)
            )}
          </p>

          {/* NQG-RQ-31: taxonomy path chips */}
          {(q.taxonomyPaths || []).length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap mt-2">
              {(q.taxonomyPaths || []).map((path: string) => (
                <span key={path} className="inline-flex px-2 py-0.5 rounded-md bg-purple-50 border border-purple-200 text-[10px] font-semibold text-purple-700">
                  {path}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          {/* Metadata Pills */}
          <div className="hidden xl:flex items-center gap-3 mr-3">
            {q.correctAnswer && (
              <div className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-green-50 border border-green-200">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-xs font-bold text-green-700">{q.correctAnswer}</span>
              </div>
            )}
            {q.estimatedTime && (
              <div className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200">
                <Timer className="w-4 h-4 text-gray-600" />
                <span className="text-xs font-semibold text-gray-700">{formatTime(q.estimatedTime)}</span>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-1 bg-white rounded-xl border-2 border-gray-200 p-1 shadow-sm">
            <button
              onClick={onView}
              className="p-2.5 text-gray-400 hover:text-[#1e73be] hover:bg-blue-50 rounded-lg transition-all hover:scale-110"
              title="View Details"
            >
              <Eye className="w-4 h-4" />
            </button>
            <button
              onClick={onEdit}
              className="p-2.5 text-gray-400 hover:text-[#ea7500] hover:bg-orange-50 rounded-lg transition-all hover:scale-110"
              title="Edit Question"
            >
              <Edit3 className="w-4 h-4" />
            </button>
            <button
              onClick={onFlag}
              className={`p-2.5 rounded-lg transition-all hover:scale-110 ${
                q.validationStatus === 'ai_wrong'
                  ? 'text-red-600 bg-red-50'
                  : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
              }`}
              title="Flag as AI Wrong"
            >
              <ShieldAlert className="w-4 h-4" />
            </button>
            <button
              onClick={onDelete}
              className="p-2.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all hover:scale-110"
              title="Delete Question"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>

          {/* Expand/Collapse Button */}
          <button
            className="p-3 text-gray-400 hover:text-[#1e73be] hover:bg-blue-50 rounded-xl border-2 border-gray-200 transition-all hover:scale-110 bg-white shadow-sm"
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
            title={isExpanded ? "Collapse" : "Expand"}
          >
            {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Expanded */}
      {isExpanded && (
        <div className="px-6 pb-6 border-t-2 border-[#1e73be]/20 pt-6 space-y-5 bg-gradient-to-b from-blue-50/30 to-transparent">

          {/* Question ID - Expanded View */}
          {q._id && (
            <div className="flex items-center gap-2 p-3 bg-white rounded-xl border-2 border-blue-200 shadow-sm">
              <span className="text-xs font-semibold text-gray-600">Question ID:</span>
              <code className="text-xs font-mono text-gray-900 bg-gray-50 px-3 py-1.5 rounded border border-gray-200 select-all">
                {q._id}
              </code>
            </div>
          )}

          {/* ── Case Study Section ── */}
          {q.type === 'case_study' && (
            <>
              {q.caseTitle && (
                <div className="flex items-center gap-3 mb-3 p-3 bg-gradient-to-r from-purple-100 to-indigo-100 rounded-xl border-2 border-purple-200 shadow-sm">
                  <div className="p-2 bg-white rounded-lg shadow-sm">
                    <BookOpen className="w-5 h-5 text-purple-600" />
                  </div>
                  <h4 className="text-base font-bold text-purple-900">{q.caseTitle}</h4>
                </div>
              )}
              {q.caseContext && (
                <div className="bg-gradient-to-br from-purple-50 via-purple-50/50 to-indigo-50/30 rounded-2xl p-5 border-2 border-purple-200 shadow-md">
                  <div className="flex items-center gap-2 mb-3">
                    <FileText className="w-4 h-4 text-purple-700" />
                    <p className="text-xs font-bold text-purple-700 uppercase tracking-wider">Case Context</p>
                  </div>
                  <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap"><RichText value={q.caseContext} /></p>
                </div>
              )}
              {q.caseData && Object.keys(q.caseData).length > 0 && (
                <div className="bg-gradient-to-br from-purple-50/50 via-violet-50/30 to-indigo-50/20 rounded-2xl p-5 border-2 border-purple-200 shadow-md">
                  <div className="flex items-center gap-2 mb-3">
                    <Database className="w-4 h-4 text-purple-700" />
                    <p className="text-xs font-bold text-purple-700 uppercase tracking-wider">Case Data</p>
                  </div>
                  <CaseDataTable data={q.caseData} />
                </div>
              )}
              {/* Sub-Questions Summary */}
              {q.subQuestions && q.subQuestions.length > 0 && (
                <div className="bg-gradient-to-br from-purple-100 via-indigo-100/70 to-blue-100/50 rounded-2xl p-4 border-2 border-purple-300 shadow-md">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <BookOpen className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-purple-900 uppercase tracking-wider">
                        {q.subQuestions.length} Sub-Questions
                      </p>
                      <p className="text-xs text-purple-700 mt-0.5">
                        {q.subQuestions.filter((sq: any) => sq.type === 'multiple_choice').length} MCQ • {' '}
                        {q.subQuestions.filter((sq: any) => sq.type === 'short_answer').length} Short Answer
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── Interactive Section ── */}
          {q.type === 'interactive' && (
            <div className="bg-gradient-to-br from-orange-50 via-orange-50/50 to-amber-50/30 rounded-2xl p-6 border-2 border-orange-200 shadow-md">
              <div className="flex items-center gap-2 mb-5">
                <Zap className="w-5 h-5 text-orange-600" />
                <p className="text-base font-bold text-gray-800">
                  Interactive Decision Tree <span className="text-orange-600">({q.prompts?.length || 0} steps)</span>
                </p>
              </div>

              {/* Use Interactive Player for actual step-by-step interaction */}
              <InteractiveQuestionPlayer
                question={{
                  question_id: q._id || q.questionId || 'interactive-q',
                  question_type: 'interactive',
                  initialContext: q.initialContext || '',
                  prompts: q.prompts || [],
                  optimalPath: q.optimalPath || [],
                  oeCode: q.oeCode,
                  difficulty: q.difficulty,
                  exam_id: q.examId
                }}
              />
            </div>
          )}

          {/* ── Question Stem (all types) ── */}
          <div className="bg-gradient-to-br from-blue-50 via-blue-50/50 to-indigo-50/30 rounded-2xl p-5 border-2 border-blue-200 shadow-md">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="w-4 h-4 text-blue-700" />
              <p className="text-xs font-bold text-blue-700 uppercase tracking-wider">
                {q.type === 'interactive' ? 'Stem / Command' : 'Question'}
              </p>
            </div>
            <p className="text-base text-gray-900 leading-relaxed whitespace-pre-wrap font-medium"><RichText value={q.stem} /></p>
          </div>

          {/* ── MCQ Alternatives ── */}
          {q.alternatives?.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Target className="w-4 h-4 text-gray-700" />
                <p className="text-sm font-bold text-gray-700">Answer Options</p>
              </div>
              <div className="space-y-3">
                {q.alternatives.map((alt: any, i: number) => (
                  <div
                    key={i}
                    className={`group relative flex items-start gap-4 rounded-2xl px-5 py-4 border-2 transition-all hover:scale-[1.02] ${
                      alt.isCorrect
                        ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-300 shadow-md'
                        : 'bg-white border-gray-200 hover:border-gray-300 shadow-sm'
                    }`}
                  >
                    <span className={`flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center text-sm font-bold shadow-md transition-transform group-hover:scale-110 ${
                      alt.isCorrect
                        ? 'bg-gradient-to-br from-green-500 to-emerald-600 text-white'
                        : 'bg-gradient-to-br from-gray-100 to-gray-200 text-gray-700'
                    }`}>
                      {alt.letter || String.fromCharCode(65 + i)}
                    </span>
                    <div className="flex-1">
                      <span className={`text-sm leading-relaxed block ${
                        alt.isCorrect ? 'text-green-900 font-semibold' : 'text-gray-800'
                      }`}>
                        <RichText value={ensureEndPeriodRich(alt.text)} />
                      </span>
                    </div>
                    {alt.isCorrect && (
                      <div className="flex-shrink-0">
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-green-500 text-white rounded-lg shadow-md">
                          <CheckCircle className="w-4 h-4" />
                          <span className="text-xs font-bold">Correct</span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Short Answer Rubric ── */}
          {q.rubric && (q.rubric.fullMarks || q.rubric.partial || q.rubric.zero) && (
            <div className="bg-gradient-to-br from-amber-50 via-amber-50/50 to-yellow-50/30 rounded-2xl p-5 border-2 border-amber-200 shadow-md">
              <div className="flex items-center gap-2 mb-4">
                <Award className="w-4 h-4 text-amber-700" />
                <p className="text-xs font-bold text-amber-700 uppercase tracking-wider">Scoring Rubric</p>
              </div>
              <div className="space-y-3">
                {q.rubric.fullMarks && (
                  <div className="flex items-start gap-3 p-4 bg-white rounded-xl border-2 border-green-200 shadow-sm">
                    <span className="flex-shrink-0 w-8 h-8 rounded-xl bg-gradient-to-br from-green-100 to-green-200 flex items-center justify-center shadow-sm">
                      <CheckCircle className="w-4 h-4 text-green-700" />
                    </span>
                    <div className="flex-1">
                      <p className="text-xs font-bold text-green-800 uppercase mb-1">Full Marks</p>
                      <p className="text-sm text-gray-800 leading-relaxed">{q.rubric.fullMarks}</p>
                    </div>
                  </div>
                )}
                {q.rubric.partial && (
                  <div className="flex items-start gap-3 p-4 bg-white rounded-xl border-2 border-amber-200 shadow-sm">
                    <span className="flex-shrink-0 w-8 h-8 rounded-xl bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center shadow-sm">
                      <AlertCircle className="w-4 h-4 text-amber-700" />
                    </span>
                    <div className="flex-1">
                      <p className="text-xs font-bold text-amber-800 uppercase mb-1">Partial Credit</p>
                      <p className="text-sm text-gray-800 leading-relaxed">{q.rubric.partial}</p>
                    </div>
                  </div>
                )}
                {q.rubric.zero && (
                  <div className="flex items-start gap-3 p-4 bg-white rounded-xl border-2 border-red-200 shadow-sm">
                    <span className="flex-shrink-0 w-8 h-8 rounded-xl bg-gradient-to-br from-red-100 to-red-200 flex items-center justify-center shadow-sm">
                      <X className="w-4 h-4 text-red-700" />
                    </span>
                    <div className="flex-1">
                      <p className="text-xs font-bold text-red-800 uppercase mb-1">No Credit</p>
                      <p className="text-sm text-gray-800 leading-relaxed">{q.rubric.zero}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Short Answer correct answer ── */}
          {q.type === 'short_answer' && q.correctAnswer && (
            <div className="bg-gradient-to-br from-green-50 via-green-50/50 to-emerald-50/30 rounded-2xl p-5 border-2 border-green-200 shadow-md">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle className="w-4 h-4 text-green-700" />
                <p className="text-xs font-bold text-green-700 uppercase tracking-wider">Expected Answer</p>
              </div>
              <p className="text-base text-gray-900 leading-relaxed whitespace-pre-wrap font-medium">{q.correctAnswer}</p>
            </div>
          )}

          {/* ── Explanation ── */}
          {q.explanation && (
            <div className="bg-gradient-to-br from-blue-50 via-blue-50/50 to-indigo-50/30 rounded-2xl p-5 border-2 border-blue-200 shadow-md">
              <div className="flex items-center gap-2 mb-3">
                <MessageSquare className="w-4 h-4 text-blue-700" />
                <p className="text-xs font-bold text-blue-700 uppercase tracking-wider">Explanation</p>
              </div>
              <p className="text-base text-gray-800 leading-relaxed whitespace-pre-wrap"><RichText value={q.explanation} /></p>
            </div>
          )}

          {/* ── Metadata ── */}
          <MetadataSection q={q} />

          {/* ── Footer info ── */}
          <div className="flex items-center justify-between gap-4 pt-4 flex-wrap border-t-2 border-gray-200">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200">
                <Clock className="w-4 h-4 text-gray-500" />
                <span className="text-xs font-medium text-gray-700">
                  {new Date(q.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
              {q.sourceDocuments?.length > 0 && (
                <div className="flex items-center gap-2 px-3 py-2 bg-indigo-50 rounded-lg border border-indigo-200">
                  <FileText className="w-4 h-4 text-indigo-600" />
                  <span className="text-xs font-medium text-indigo-700">
                    {q.sourceDocuments.length} source{q.sourceDocuments.length !== 1 ? 's' : ''}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Detail Modal ────────────────────────────────────────────────────────────

function QuestionDetailModal({
  question: q,
  onClose,
  onEdit,
  canEdit = true,
  onOpenVersions,
}: {
  question: any;
  onClose: () => void;
  onEdit: () => void;
  canEdit?: boolean;
  onOpenVersions?: () => void;
}) {
  const diffMap: Record<string, string> = { easy: 'success', medium: 'warning', hard: 'error' };
  const tc = TYPE_CONFIG[q.type] || TYPE_CONFIG.multiple_choice;
  const TypeIcon = tc.icon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl mx-4 max-h-[90vh] flex flex-col overflow-hidden animate-in">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#1e73be] to-[#4A90C8] px-6 py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-xl">
                <TypeIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">
                  {q.type === 'case_study' && q.caseTitle ? q.caseTitle : 'Question Detail'}
                </h2>
                {/* Question ID */}
                {q._id && (
                  <div className="mt-1 mb-1">
                    <span className="text-[10px] font-mono text-white/60">ID: {q._id}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  {q.oeCode && (
                    <span className="text-xs font-mono font-bold text-white/80 bg-white/20 px-2 py-0.5 rounded">
                      {q.oeCode}
                    </span>
                  )}
                  {/* NQG-RQ-29: portfolio tag(s) */}
                  {(q.portfolios || []).map((pf: string) => (
                    <span key={pf} className="text-xs font-bold text-white/90 bg-purple-500/40 px-2 py-0.5 rounded">
                      {pf}
                    </span>
                  ))}
                  <span className="text-xs text-white/60">{tc.label}</span>
                  {q.hasCalculation && (
                    <span className="text-xs text-amber-300 bg-white/10 px-1.5 py-0.5 rounded font-semibold">Calc</span>
                  )}
                </div>
              </div>
            </div>
            <button onClick={onClose} className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-xl transition-all">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5 custom-scrollbar">
          {/* Badges */}
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant={(diffMap[q.difficulty] || 'neutral') as any}>{q.difficulty}</Badge>
            <StatusBadge status={q.status || 'draft'} />
            {q.correctAnswer && q.type !== 'interactive' && <Badge variant="success">Answer: {q.correctAnswer}</Badge>}
            {q.cognitiveLevel && (
              <span className={`text-xs font-bold px-2 py-0.5 rounded ${COGNITIVE_LABELS[q.cognitiveLevel]?.color || 'bg-gray-50 text-gray-600'}`}>
                {COGNITIVE_LABELS[q.cognitiveLevel]?.label || q.cognitiveLevel}
              </span>
            )}
            {q.estimatedTime && (
              <span className="text-xs font-medium text-gray-500 bg-gray-50 px-2 py-0.5 rounded flex items-center gap-1">
                <Timer className="w-3 h-3" /> {formatTime(q.estimatedTime)}
              </span>
            )}
            {q.mcqFormat && (
              <span className="text-xs font-medium text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded">{q.mcqFormat}</span>
            )}
            {!q.rootQuestionId && (q.versionsCount ?? 0) > 0 && onOpenVersions && (
              <button
                onClick={onOpenVersions}
                className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100 transition-colors"
              >
                <GitBranch className="w-3 h-3" />
                {q.versionsCount} version{q.versionsCount > 1 ? 's' : ''}
              </button>
            )}
          </div>

          {/* NQG-RQ-31: taxonomy path (stamped at generation time) */}
          {(q.taxonomyPaths || []).length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap">
              {(q.taxonomyPaths || []).map((path: string) => (
                <span key={path} className="inline-flex px-2.5 py-1 rounded-lg bg-purple-50 border border-purple-200 text-xs font-semibold text-purple-700">
                  {path}
                </span>
              ))}
            </div>
          )}

          {/* Version identity banner (NQG-RQ-27) — shown when this question IS a version */}
          {q.rootQuestionId && (
            <div className="flex items-center justify-between gap-3 p-3 bg-purple-50 rounded-xl border border-purple-200">
              <div className="flex items-center gap-2 min-w-0">
                <GitBranch className="w-4 h-4 text-purple-600 flex-shrink-0" />
                <span className="text-sm font-semibold text-purple-800 truncate">
                  Version Q1.{q.versionNumber}
                  {q.versionCategory
                    ? ` (${q.versionCategory.charAt(0).toUpperCase()}${q.versionCategory.slice(1)})`
                    : ''}
                </span>
              </div>
              {onOpenVersions && (
                <button
                  onClick={onOpenVersions}
                  className="text-xs font-semibold text-purple-700 hover:text-purple-900 underline flex-shrink-0"
                >
                  View original & all versions
                </button>
              )}
            </div>
          )}

          {/* Question ID - Prominent Display (hidden for interactive - already shown in header) */}
          {q._id && q.type !== 'interactive' && (
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl border border-gray-200">
              <span className="text-xs font-semibold text-gray-600">Question ID:</span>
              <code className="text-xs font-mono text-gray-900 bg-white px-3 py-1 rounded border border-gray-200 select-all">
                {q._id}
              </code>
            </div>
          )}

          {/* Generation audit info — who generated it and with which prompt */}
          {(q.generatedByUser || q.versionCreatedBy || q.customPrompt || q.versionConceptPrompt) && (
            <div className="bg-sky-50/60 rounded-xl p-4 border border-sky-100 space-y-2">
              <p className="text-xs font-bold text-sky-700 uppercase tracking-wider">Generation Info</p>
              <div className="flex items-center gap-4 flex-wrap text-xs text-gray-700">
                {(q.generatedByUser || q.versionCreatedBy) && (
                  <span>
                    <span className="font-semibold text-gray-500">Generated by:</span>{' '}
                    {q.generatedByUser || q.versionCreatedBy}
                  </span>
                )}
                {q.createdAt && (
                  <span>
                    <span className="font-semibold text-gray-500">On:</span>{' '}
                    {new Date(q.createdAt).toLocaleString()}
                  </span>
                )}
              </div>
              {(q.customPrompt || q.versionConceptPrompt) && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 mb-1">
                    {q.versionConceptPrompt ? 'Version prompt used:' : 'Prompt used:'}
                  </p>
                  <p className="text-xs text-gray-800 bg-white rounded-lg border border-sky-100 p-2 whitespace-pre-wrap leading-relaxed">
                    {q.versionConceptPrompt || q.customPrompt}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ── Case Study ── */}
          {q.type === 'case_study' && (
            <>
              {q.caseContext && (
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50/20 rounded-xl p-5 border border-purple-100">
                  <p className="text-xs font-bold text-purple-700 uppercase tracking-wider mb-2">Case Context</p>
                  <p className="text-sm text-gray-900 leading-relaxed whitespace-pre-wrap"><RichText value={q.caseContext} /></p>
                </div>
              )}
              {q.caseData && Object.keys(q.caseData).length > 0 && (
                <div className="bg-gradient-to-r from-purple-50/50 to-violet-50/20 rounded-xl p-5 border border-purple-100">
                  <p className="text-xs font-bold text-purple-700 uppercase tracking-wider mb-2">Case Data</p>
                  <CaseDataTable data={q.caseData} />
                </div>
              )}
              {/* Sub-Questions (ANBIMA Format: 3-5 questions per case) */}
              {q.subQuestions && q.subQuestions.length > 0 && (
                <div className="bg-gradient-to-br from-purple-50 via-indigo-50/50 to-blue-50/30 rounded-2xl p-6 border-2 border-purple-200 shadow-lg">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="p-2 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl shadow-md">
                      <BookOpen className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-base font-bold text-purple-900">
                      Sub-Questions ({q.subQuestions.length})
                    </h3>
                  </div>
                  <div className="space-y-6">
                    {q.subQuestions.map((sq: any, sqIdx: number) => (
                      <div key={sqIdx} className="bg-white rounded-xl p-5 border-2 border-purple-100 shadow-sm hover:shadow-md transition-shadow">
                        {/* Sub-Question Header */}
                        <div className="flex items-center gap-3 mb-4 pb-3 border-b-2 border-purple-100">
                          <span className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white font-bold shadow-md">
                            {sq.caseQuestionNumber || sqIdx + 1}
                          </span>
                          <div className="flex-1">
                            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold ${
                              sq.type === 'multiple_choice'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-emerald-100 text-emerald-700'
                            }`}>
                              {sq.type === 'multiple_choice' ? <CheckCircle className="w-3 h-3" /> : <Edit3 className="w-3 h-3" />}
                              {sq.type === 'multiple_choice' ? 'Multiple Choice' : 'Short Answer'}
                            </span>
                          </div>
                        </div>

                        {/* Sub-Question Command/Stem */}
                        <div className="mb-4">
                          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Question {sq.caseQuestionNumber || sqIdx + 1}</p>
                          <p className="text-sm text-gray-900 leading-relaxed font-medium"><RichText value={sq.comando} /></p>
                        </div>

                        {/* Alternatives (for multiple_choice) */}
                        {sq.type === 'multiple_choice' && sq.alternatives && sq.alternatives.length > 0 && (
                          <div className="space-y-2 mb-4">
                            {sq.alternatives.map((alt: any, altIdx: number) => (
                              <div
                                key={altIdx}
                                className={`flex items-start gap-3 rounded-lg px-4 py-3 border-2 transition-all ${
                                  alt.isCorrect
                                    ? 'bg-green-50 border-green-300 shadow-sm'
                                    : 'bg-gray-50 border-gray-200'
                                }`}
                              >
                                <span className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold ${
                                  alt.isCorrect ? 'bg-green-500 text-white shadow-sm' : 'bg-white text-gray-600 border border-gray-300'
                                }`}>
                                  {alt.letter}
                                </span>
                                <p className={`text-sm flex-1 ${alt.isCorrect ? 'text-green-900 font-medium' : 'text-gray-700'}`}>
                                  <RichText value={alt.text} />
                                </p>
                                {alt.isCorrect && <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Explanation */}
                        {sq.explanation && (
                          <div className="bg-gradient-to-r from-blue-50 to-indigo-50/20 rounded-lg p-4 border border-blue-200">
                            <p className="text-xs font-bold text-blue-700 uppercase tracking-wider mb-2">Explanation</p>
                            <p className="text-sm text-gray-800 leading-relaxed"><RichText value={sq.explanation} /></p>
                          </div>
                        )}

                        {/* Correct Answer (for short_answer) */}
                        {sq.type === 'short_answer' && sq.correctAnswer && (
                          <div className="bg-gradient-to-r from-green-50 to-emerald-50/20 rounded-lg p-4 border border-green-200">
                            <p className="text-xs font-bold text-green-700 uppercase tracking-wider mb-2">Correct Answer</p>
                            <p className="text-sm text-gray-800 leading-relaxed font-medium">{sq.correctAnswer}</p>
                          </div>
                        )}

                        {/* Rubric (for short_answer) */}
                        {sq.type === 'short_answer' && sq.rubric && (sq.rubric.fullMarks || sq.rubric.partial || sq.rubric.zero) && (
                          <div className="bg-gradient-to-r from-amber-50 to-yellow-50/20 rounded-lg p-4 border border-amber-200 mt-3">
                            <p className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-2">Rubric</p>
                            <div className="space-y-1.5 text-xs">
                              {sq.rubric.fullMarks && (
                                <div className="flex items-start gap-2">
                                  <CheckCircle className="w-3.5 h-3.5 text-green-600 flex-shrink-0 mt-0.5" />
                                  <span className="text-gray-700"><strong>Full Marks:</strong> {sq.rubric.fullMarks}</span>
                                </div>
                              )}
                              {sq.rubric.partial && (
                                <div className="flex items-start gap-2">
                                  <AlertCircle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
                                  <span className="text-gray-700"><strong>Partial:</strong> {sq.rubric.partial}</span>
                                </div>
                              )}
                              {sq.rubric.zero && (
                                <div className="flex items-start gap-2">
                                  <XCircle className="w-3.5 h-3.5 text-red-600 flex-shrink-0 mt-0.5" />
                                  <span className="text-gray-700"><strong>Zero Marks:</strong> {sq.rubric.zero}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── Interactive ── */}
          {q.type === 'interactive' && (
            <div className="bg-gradient-to-r from-orange-50 to-amber-50/20 rounded-xl p-5 border border-orange-100">
              <p className="text-xs font-bold text-orange-700 uppercase tracking-wider mb-4">
                Interactive Decision Tree ({q.prompts?.length || 0} steps)
              </p>

              {/* Use Interactive Player for actual step-by-step interaction */}
              <InteractiveQuestionPlayer
                question={{
                  question_id: q._id || q.questionId || 'interactive-q',
                  question_type: 'interactive',
                  initialContext: q.initialContext || '',
                  prompts: q.prompts || [],
                  optimalPath: q.optimalPath || [],
                  oeCode: q.oeCode,
                  difficulty: q.difficulty,
                  exam_id: q.examId
                }}
              />
            </div>
          )}

          {/* Question stem (hidden for case_study since questions are in sub-questions) */}
          {q.type !== 'case_study' && (
            <div className="bg-gradient-to-r from-gray-50 to-blue-50/20 rounded-xl p-5 border border-gray-100">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                {q.type === 'interactive' ? 'Stem / Command' : 'Question'}
              </p>
              <p className="text-sm text-gray-900 leading-relaxed whitespace-pre-wrap"><RichText value={q.stem} /></p>
            </div>
          )}

          {/* MCQ Alternatives (hidden for case_study since alternatives are in sub-questions) */}
          {q.type !== 'case_study' && q.alternatives?.length > 0 && (
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Alternatives</p>
              <div className="space-y-2">
                {q.alternatives.map((alt: any, i: number) => (
                  <div
                    key={i}
                    className={`flex items-start gap-3 rounded-xl px-4 py-3 border-2 ${
                      alt.isCorrect ? 'bg-green-50 border-green-200' : 'bg-white border-gray-100'
                    }`}
                  >
                    <span className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${
                      alt.isCorrect ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {alt.letter || String.fromCharCode(65 + i)}
                    </span>
                    <div className="flex-1">
                      <p className={`text-sm ${alt.isCorrect ? 'text-green-800 font-medium' : 'text-gray-700'}`}><RichText value={ensureEndPeriodRich(alt.text)} /></p>
                      {/* {alt.explanation && (
                        <p className="text-xs text-gray-500 mt-1 italic">{alt.explanation}</p>
                      )} */}
                      {/* {alt.whyWrong && !alt.isCorrect && (
                        <p className="text-xs text-red-500 mt-1 italic">{alt.whyWrong}</p>
                      )} */}
                      {/* {alt.errorType && !alt.isCorrect && (
                        <span className="inline-block text-[10px] font-semibold text-red-400 bg-red-50 px-1.5 py-0.5 rounded mt-1">
                          Error: {alt.errorType}
                        </span>
                      )} */}
                    </div>
                    {alt.isCorrect && <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Short Answer Rubric (hidden for case_study since rubrics are in sub-questions) */}
          {q.type !== 'case_study' && q.rubric && (q.rubric.fullMarks || q.rubric.partial || q.rubric.zero) && (
            <div className="bg-gradient-to-r from-amber-50 to-yellow-50/20 rounded-xl p-5 border border-amber-100">
              <p className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-3">Rubric</p>
              <div className="space-y-2">
                {q.rubric.fullMarks && (
                  <div className="flex items-start gap-3 p-4 bg-white rounded-xl border-2 border-green-200 shadow-sm">
                    <span className="flex-shrink-0 w-8 h-8 rounded-xl bg-gradient-to-br from-green-100 to-green-200 flex items-center justify-center shadow-sm">
                      <CheckCircle className="w-4 h-4 text-green-700" />
                    </span>
                    <div className="flex-1">
                      <p className="text-xs font-bold text-green-800 uppercase mb-1">Full Marks</p>
                      <p className="text-sm text-gray-800 leading-relaxed">{q.rubric.fullMarks}</p>
                    </div>
                  </div>
                )}
                {q.rubric.partial && (
                  <div className="flex items-start gap-3 p-4 bg-white rounded-xl border-2 border-amber-200 shadow-sm">
                    <span className="flex-shrink-0 w-8 h-8 rounded-xl bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center shadow-sm">
                      <AlertCircle className="w-4 h-4 text-amber-700" />
                    </span>
                    <div className="flex-1">
                      <p className="text-xs font-bold text-amber-800 uppercase mb-1">Partial Credit</p>
                      <p className="text-sm text-gray-800 leading-relaxed">{q.rubric.partial}</p>
                    </div>
                  </div>
                )}
                {q.rubric.zero && (
                  <div className="flex items-start gap-3 p-4 bg-white rounded-xl border-2 border-red-200 shadow-sm">
                    <span className="flex-shrink-0 w-8 h-8 rounded-xl bg-gradient-to-br from-red-100 to-red-200 flex items-center justify-center shadow-sm">
                      <X className="w-4 h-4 text-red-700" />
                    </span>
                    <div className="flex-1">
                      <p className="text-xs font-bold text-red-800 uppercase mb-1">No Credit</p>
                      <p className="text-sm text-gray-800 leading-relaxed">{q.rubric.zero}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Short Answer expected answer */}
          {q.type === 'short_answer' && q.correctAnswer && (
            <div className="bg-green-50 rounded-xl p-5 border border-green-100">
              <p className="text-xs font-bold text-green-700 uppercase tracking-wider mb-2">Expected Answer</p>
              <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">{q.correctAnswer}</p>
            </div>
          )}

          {/* Explanation (hidden for case_study since explanations are in sub-questions) */}
          {q.type !== 'case_study' && q.explanation && (
            <div className="bg-blue-50/50 rounded-xl p-5 border border-blue-100">
              <p className="text-xs font-bold text-blue-700 uppercase tracking-wider mb-2">Explanation</p>
              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap"><RichText value={q.explanation} /></p>
            </div>
          )}

          {/* Metadata */}
          <MetadataSection q={q} />

          {/* Source documents */}
          {q.sourceDocuments && q.sourceDocuments.length > 0 && (
            <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Source Documents</p>
              <div className="space-y-1">
                {q.sourceDocuments.map((doc: any, idx: number) => (
                  <p key={idx} className="text-sm text-gray-700 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    {doc.title || doc.filename}
                  </p>
                ))}
              </div>
            </div>
          )}

          {/* Created date */}
          <div className="flex items-center gap-4 text-xs text-gray-400 pt-2 border-t border-gray-100">
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Created: {new Date(q.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </span>
            {q.generatedBy && (
              <span className="flex items-center gap-1">
                <Zap className="w-3 h-3" />
                {q.generatedBy}
              </span>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex-shrink-0">
          <button onClick={onClose} className="btn-secondary flex-1 py-2.5 rounded-xl text-sm font-semibold">
            Close
          </button>
          {/* <button
            onClick={onFlag}
            className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold bg-red-50 text-red-600 border-2 border-red-200 hover:bg-red-100 transition-colors"
          >
            <ShieldAlert className="w-4 h-4" />
            AI Wrong
          </button> */}
          {canEdit && (
            <button onClick={onEdit} className="btn-primary flex-1 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2">
              <Edit3 className="w-4 h-4" />
              Edit Question
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default PdfQuestionBank;


